import React, { useEffect, useState, useCallback, useRef } from "react";
import styled, { ThemeProvider } from "styled-components";

import { toast, Flip } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import LoadingComponent from "components/Loading";

import { SIZE, isFontSize } from "constants/themes";
import { MESSAGE_UPDATED, POSITION_RELOAD } from "constants/webview";
import { renderCsCategory } from "constants/cses";
import { renderReviewCategory } from "constants/reviews";

import {
  STATUS_PICKUP,
  STATUS_DELIVERY_WAITING,
  STATUS_DELIVERY_POSTPONED,
  STATUS_DELIVERY_STARTED,
  STATUS_CS,
  DELIVERY_TYPE_NORMAL,
  DELIVERY_TYPE_PICKUP,
  DELIVERY_TYPE_RETURN,
} from "Models";

import {
  useCommonStore,
  useDeliveriesStore,
  useLoadingStore,
  useMapStore,
  useModalStore,
  useOrderStore,
  useReallocationStore,
  useRidersStore,
  useRoutesStore,
} from "stores/hooks";

import { getOrder, getOrderWithOrderMode, getRearrange } from "utils/common";
import { initMap } from "utils/map";
import { appVersion } from "utils/params";

import BottomSummary from "components/Map/BottomSummary";
import BottomButtons from "components/Map/BottomButtons";
import CountSummary from "components/Map/CountSummary";
import OrderGuide from "components/Map/OrderGuide";
import RearrangeGuide from "components/Map/RearrangeGuide";
import TrackingMarkers from "components/Map/TrackingMarkers";

import OrderConfirmModal from "modals/OrderConfirmModal";
import ConfirmModal from "modals/ConfirmModal";
import ResetConfirmModal from "modals/ResetConfirmModal";
import RidersModal from "modals/RidersModal";
import SettingModal from "modals/SettingModal";
// import SetGoalAddressModal from "modals/SetGoalAddressModal";

import { TMAP } from "constants/links";

import { checkNearbyMarkers } from "utils/deliveries";

import { ReactComponent as IconCurrent } from "assets/images/map/icon-current.svg";
import { ReactComponent as IconRefresh } from "assets/images/map/icon-refresh.svg";
import { ReactComponent as IconSetting } from "assets/images/map/icon-setting.svg";
import * as mapImages from "assets/images/map/urls";

const MapContainer = styled.div`
  position: relative;

  .info-title {
    font-weight: bold;
    font-size: ${({ theme }) => theme.font16}px;
    display: flex;
    flex-wrap: wrap;
  }

  .info-spot {
    font-weight: bold;
    font-size: ${({ theme }) => theme.font14}px;
  }

  .info-base-address {
    font-weight: bold;
    font-size: ${({ theme }) => theme.font14}px;
  }

  .info-address {
    color: #828182;
    font-size: ${({ theme }) => theme.font14}px;
  }

  .info-address.bold {
    font-weight: 700;
    color: #000000;
  }

  .info-reqeusted-time {
    color: red;
    font-size: ${({ theme }) => theme.font14}px;
  }

  .info-button-wrap {
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 0;
    width: 100%;
    background-color: #fff;
    z-index: 10;
  }

  .btn {
    flex: 1;
    background-color: #2bd723;
    font-size: 16px;
    padding: 7px 10px;
    color: #fff;
    width: 100%;
    border-radius: 0;
  }

  .btn.btn-border {
    background-color: transparent;
    border: 1px solid #2bd723;
    color: #2bd723;
  }

  .marker-address {
    font-size: ${({ theme }) => theme.font10}px;
    position: absolute;
    width: 80px;
    text-align: center;
    top: 40px;
    padding: 3px;
    background-color: rgba(0, 0, 0, 0.1);
  }

  .marker-order {
    font-size: ${({ theme }) => theme.font12}px;
    position: absolute;
    top: 18px;
    right: 24px;
    color: #fff;
    background-color: #d84d50;
    border: 1px solid #fff;
    border-radius: 2px;
    min-width: 20px;
    text-align: center;
  }

  .marker-memo {
    font-size: ${({ theme }) => theme.font12}px;
    position: absolute;
    top: -2px;
    right: 24px;
    color: #fff;
    background-color: #fc3f00;
    border: 1px solid #fff;
    border-radius: 100%;
    min-width: 19px;
    text-align: center;

    &.cjoyquickapi {
      background-color: #fc3f00;
    }

    &.kurly {
      background-color: #672091;
    }
  }
`;

const Button = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1.7px 4px 0 rgba(0, 0, 0, 0.2);
`;

// 현재 위치 조회
const ButtonCurrent = styled(Button)`
  svg {
    width: 24px;
    height: 24px;
  }
`;

// 새로고침
const ButtonReload = styled(Button)`
  svg {
    width: 16px;
    height: 16px;
  }
`;

// 설정
const ButtonSetting = styled(Button)`
  svg {
    width: 22px;
    height: 22px;
  }
`;

// 지도
const MapConatiner = styled.div`
  width: 100%;
  height: 100vh;
`;

const MapRetryWrap = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2000;
  transform: translate(-50%, -50%);
  text-align: center;
  padding: 16px;
  background: #fff;
  border: 1px solid #ccc;
`;

const MapRetryButton = styled.button`
  margin-top: 12px;
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #333;
  cursor: pointer;
  font-size: 16px;
`;

const TopWrap = styled.div`
  position: absolute;
  top: 10px;
  z-index: 1000;
  width: 100%;
  background: rgba(0, 0, 0, 0.15);
  padding: 6px 0;
  ${({ hide }) => hide && `display: none;`};
`;

const BottomWrap = styled.div`
  position: absolute;
  bottom: 12px;
  z-index: 1000;
  width: 100%;

  ${({ hide }) => hide && `display: none;`}
`;

const BottomButtonsWrap = styled.div`
  padding-left: 12px;
  padding-right: 12px;
  display: flex;
  justify-content: space-between;
`;

const OptimizingBackground = styled.div`
  position: fixed;
  top: 56px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.25);
  color: #fff;
  padding: 4px 8px;
  border-radius: 5px;
  font-size: 13px;
  z-index: 2000;
  white-space: nowrap;
`;

let markers = [];
let markersSoojione = [];
let markersBaseAddressEntryPoint = [];
let polylines = [];
let polylinesBaseAddressEntryPoint = [];
let infoWindows = [];

toast.configure({
  transition: Flip,
  autoClose: 1000,
  draggable: false,
  closeButton: false,
});

const bookIdKey = (bookId) => String(bookId);

const hasSequenceAssigned = (route) => route?.sequence != null;

const ROUTE_INIT_RETRY_LIMIT = 3;
const ROUTE_INIT_RETRY_MS = 1000;

function Map() {
  const {
    count,

    statuses,

    markerList,
    markerListWithSameplace,
    deliveryStartList,
    orderList,

    fetched,

    deliveriesAllocated,

    limitedHoursCount,

    deliveryStartBookIds,
    deliveryStartBookIdsObj,

    scanSessions,
  } = useDeliveriesStore().state;
  const { size } = useCommonStore().state;
  const { ...commonActions } = useCommonStore();

  const { ...deliveriesActions } = useDeliveriesStore();

  const { loading } = useLoadingStore().state;
  const { startLoading, finishLoading } = useLoadingStore();

  const { isOrderMode, newOrderList } = useOrderStore().state;
  const { ...orderActions } = useOrderStore();

  const { isRearrangeMode, rearrangeDescription, selectedDeliveries } =
    useReallocationStore().state;
  const { ...reallocationActions } = useReallocationStore();

  const {
    map,
    showHideMarkers,
    showMarkerAddress,
    orderingNumberColor,
    currentPosition,
    currentCenter,
    linkOthers,
  } = useMapStore().state;
  const { ...mapActions } = useMapStore();

  const { modals } = useModalStore().state;
  const { openModal, closeModal } = useModalStore();

  const { ...ridersActions } = useRidersStore();

  const { ...routesActions } = useRoutesStore();

  // TODO: 리로드 함수로 변경
  const [reload, setReload] = useState(false);

  const [ready, setReady] = useState(
    Number(appVersion) < 44.3 || Number(appVersion) >= 46 ? true : false,
  );
  const [mapLoadFailed, setMapLoadFailed] = useState(false);
  const lastRnMessageRef = useRef({ key: null, time: 0 });

  // 45.8 등 44.3~46 구간: 네이티브 메시지 유실 대비 fallback
  useEffect(() => {
    if (ready) return;

    const timer = setTimeout(() => {
      setReady(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [ready]);

  const handleChangeReload = useCallback((_reload = true) => {
    setReload(_reload);
  }, []);

  const hasCheckedDeliveryStartedOrdersRef = useRef(false);
  const lastCheckedDeliveryBookIdsRef = useRef(new Set());
  const [isOrderAssignmentPending, setIsOrderAssignmentPending] =
    useState(false);

  const setCheckedDeliveryStartedOrders = (value) => {
    hasCheckedDeliveryStartedOrdersRef.current = value;
  };

  const [
    deliveriesStartedWithLocationTracking,
    setDeliveriesStartedWithLocationTracking,
  ] = useState([]);

  useEffect(() => {
    if (!ready) return;

    fetchAll();
    fetchCount();
    fetchLimitedHoursCount();
  }, [statuses, ready]);

  useEffect(() => {
    if (!reload) return;

    if (ready) {
      fetchAll();
      fetchCount();
      fetchLimitedHoursCount();
    }

    handleChangeReload(false);
  }, [reload]);

  const checkDeliveryStartedOrders = (deliveriesAllocated, routesMap = {}) => {
    const deliveriesStarted = deliveriesAllocated.filter(
      (d) => d.status === STATUS_DELIVERY_STARTED,
    );
    const _deliveriesStartedWithLocationTracking = deliveriesStarted.filter(
      (d) => d.corpUserEnableRiderLocationTracking,
    );
    setDeliveriesStartedWithLocationTracking(
      _deliveriesStartedWithLocationTracking,
    );

    if (_deliveriesStartedWithLocationTracking.length === 0) return;

    // [1단계] 주소오류(위치없음) 건 선제 체크
    const addressErrorDeliveries =
      _deliveriesStartedWithLocationTracking.filter(
        (d) => d.isReceiverAddressError,
      );

    if (addressErrorDeliveries.length > 0) {
      if (isAddressErrorModalOpen.current) return; // 중복 방지
      isAddressErrorModalOpen.current = true;

      openModal(
        <ConfirmModal
          key="address-error-modal"
          description={`주소오류로 위치값이 누락된 배송출발건 ${addressErrorDeliveries.length}건이 있습니다. 배송목록 > 실배 목록에서 (위치없음) 접수건 주소 확인 후 주소수정하거나, 배송연기/배송대기 변경 후 지도화면에 접속해주십시오.`}
          onConfirmText="배송목록이동"
          onConfirm={() => {
            isAddressErrorModalOpen.current = false; // 닫힐 때 초기화
            closeModal();
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: "list" }),
            );
          }}
        />,
      );
      return;
    }

    // [2단계] 이미 순서 지정된 경우 팝업 스킵
    // 위치추적 대상건 전체가 routesMap에 포함되어 있어야 "순서 지정됨"으로 간주
    // 하나라도 빠져 있으면 (리셋 or 신규 추가 건) 새로 설정하도록 팝업 표시
    const hasAssignedSequence = _deliveriesStartedWithLocationTracking.every(
      (d) => hasSequenceAssigned(routesMap[bookIdKey(d.bookId)]),
    );

    if (hasAssignedSequence) {
      lastCheckedDeliveryBookIdsRef.current = new Set(
        _deliveriesStartedWithLocationTracking.map((d) => bookIdKey(d.bookId)),
      );
      setIsOrderAssignmentPending(false);
      setCheckedDeliveryStartedOrders(true);
      return;
    }

    // [3단계] 순서지정 팝업
    openModal(
      <ConfirmModal
        key="confirm-modal"
        description={`배송출발 ${_deliveriesStartedWithLocationTracking.length}건 중 배송순서 지정에서 제외할 배송건이 있으면 출발건제외 버튼을 눌러 대기상태 혹은 연기상태로 변경해주십시오.`}
        onConfirmText="출발건제외"
        onConfirm={() => {
          closeModal();
          handleClickStartRearrange({
            onlyDeliveryStart: true,
            rearrangeDescription:
              "이번 회차에 배송하지 않을 배송건은 대기/연기 상태로 변경해 주십시오.",
          });
        }}
        onCancelText="순서지정"
        onCancel={() => {
          closeModal();
          handleClickStartOrder();
        }}
      />,
    );

    setIsOrderAssignmentPending(true);

    lastCheckedDeliveryBookIdsRef.current = new Set(
      _deliveriesStartedWithLocationTracking.map((d) => bookIdKey(d.bookId)),
    );
    setCheckedDeliveryStartedOrders(true);
  };

  const [deliveryRoutesMap, setDeliveryRoutesMap] = useState({});
  const deliveryRoutesMapRef = useRef({});

  useEffect(() => {
    deliveryRoutesMapRef.current = deliveryRoutesMap;
  }, [deliveryRoutesMap]);

  const isInitializingRoutes = useRef(false);
  const pendingInitDeliveriesRef = useRef(null);
  const routeInitRetryCountRef = useRef(0);
  const isAddressErrorModalOpen = useRef(false);

  const isOptimizingRef = useRef(false);
  const [showLoadingClose, setShowLoadingClose] = useState(false);

  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isOptimizingBackground, setIsOptimizingBackground] = useState(false);

  useEffect(() => {
    isOptimizingRef.current = isOptimizing;
  }, [isOptimizing]);

  useEffect(() => {
    if (!isOptimizing) {
      setShowLoadingClose(false);
      return;
    }
    const timer = setTimeout(() => {
      setShowLoadingClose(true);
    }, 120000); // 2분

    return () => clearTimeout(timer);
  }, [isOptimizing]);

  useEffect(() => {
    if (deliveriesAllocated.length === 0) return;

    const deliveryStartedItems = deliveriesAllocated.filter(
      (d) =>
        d.status === STATUS_DELIVERY_STARTED &&
        d.corpUserEnableRiderLocationTracking,
    );

    if (hasCheckedDeliveryStartedOrdersRef.current) {
      const hasNewDelivery = deliveryStartedItems.some(
        (d) => !lastCheckedDeliveryBookIdsRef.current.has(bookIdKey(d.bookId)),
      );

      if (hasNewDelivery) {
        setDeliveryRoutesMap({});
        deliveryRoutesMapRef.current = {};
        setCheckedDeliveryStartedOrders(false);
        lastCheckedDeliveryBookIdsRef.current = new Set();
        isInitializingRoutes.current = false;
        routeInitRetryCountRef.current = 0;
      }
    }

    initDeliveryRoutes(deliveriesAllocated);
  }, [deliveriesAllocated]);

  useEffect(() => {
    if (!deliveryRoutesMap || Object.keys(deliveryRoutesMap).length === 0) {
      mapActions.setNextDeliveryTarget(null);
      return;
    }

    const pendingStopsSorted = Object.entries(deliveryRoutesMap)
      .filter(([, v]) => hasSequenceAssigned(v) && v?.stopStatus === "PENDING")
      .sort(([, a], [, b]) => a.sequence - b.sequence);

    const nextDelivery = pendingStopsSorted.reduce((found, [id]) => {
      if (found) return found;
      const delivery = markerList.find(
        (m) => bookIdKey(m.bookId) === bookIdKey(id),
      );
      return delivery ?? null;
    }, null);

    if (
      nextDelivery?.compareReceiverLocation?.latitude &&
      nextDelivery?.compareReceiverLocation?.longitude
    ) {
      mapActions.setNextDeliveryTarget({
        latitude: Number(nextDelivery.compareReceiverLocation.latitude),
        longitude: Number(nextDelivery.compareReceiverLocation.longitude),
      });
    } else {
      mapActions.setNextDeliveryTarget(null);
    }
  }, [deliveryRoutesMap, markerList]);

  const scheduleRouteInitRetry = (deliveries) => {
    pendingInitDeliveriesRef.current = deliveries;
    window.setTimeout(() => {
      isInitializingRoutes.current = false;
      const pending = pendingInitDeliveriesRef.current;
      pendingInitDeliveriesRef.current = null;
      if (pending) {
        initDeliveryRoutes(pending);
      }
    }, ROUTE_INIT_RETRY_MS);
  };

  const buildRoutesMapFromResponse = (response) => {
    const routesMap = {};
    response.stops.forEach((stop) => {
      routesMap[bookIdKey(stop.bookId)] = {
        routeId: response.id,
        sequence: stop.sequence,
        totalStops: response.stops.length,
        stopStatus: stop.stopStatus,
        routeStatus: response.status,
        tripSequence: response.tripSequence,
        routeDate: response.routeDate,
      };
    });
    return routesMap;
  };

  const initDeliveryRoutes = async (deliveries) => {
    if (isInitializingRoutes.current) {
      pendingInitDeliveriesRef.current = deliveries;
      return;
    }

    isInitializingRoutes.current = true;
    let scheduledRetry = false;

    try {
      let currentRoutesMap = deliveryRoutesMapRef.current;
      const hasCachedRoutes =
        Object.values(currentRoutesMap).some(hasSequenceAssigned);

      const deliveryStartedItems = deliveries.filter(
        (d) =>
          d.status === STATUS_DELIVERY_STARTED &&
          d.corpUserEnableRiderLocationTracking,
      );

      if (!hasCachedRoutes && deliveryStartedItems.length > 0) {
        try {
          const response = await routesActions.fetchRouteByBookId(
            deliveryStartedItems[0].bookId,
          );

          if (response?.stops?.length) {
            currentRoutesMap = buildRoutesMapFromResponse(response);
            deliveryRoutesMapRef.current = currentRoutesMap;
            setDeliveryRoutesMap(currentRoutesMap);
          }
        } catch (e) {
          console.log(e);
          if (routeInitRetryCountRef.current < ROUTE_INIT_RETRY_LIMIT) {
            routeInitRetryCountRef.current += 1;
            scheduledRetry = true;
            scheduleRouteInitRetry(deliveries);
            return;
          }
        }
      }

      const hasAssignedSequence = deliveryStartedItems.every((d) =>
        hasSequenceAssigned(currentRoutesMap[bookIdKey(d.bookId)]),
      );

      if (hasAssignedSequence) {
        routeInitRetryCountRef.current = 0;
        if (!hasCheckedDeliveryStartedOrdersRef.current) {
          checkDeliveryStartedOrders(deliveries, currentRoutesMap);
        }
        return;
      }

      const hasPartialStops = Object.keys(currentRoutesMap).length > 0;

      if (
        hasPartialStops &&
        routeInitRetryCountRef.current < ROUTE_INIT_RETRY_LIMIT
      ) {
        routeInitRetryCountRef.current += 1;
        deliveryRoutesMapRef.current = {};
        setDeliveryRoutesMap({});
        scheduledRetry = true;
        scheduleRouteInitRetry(deliveries);
        return;
      }

      if (!hasCheckedDeliveryStartedOrdersRef.current) {
        checkDeliveryStartedOrders(deliveries, currentRoutesMap);
      }
    } finally {
      if (!scheduledRetry) {
        isInitializingRoutes.current = false;
        const pending = pendingInitDeliveriesRef.current;
        pendingInitDeliveriesRef.current = null;
        if (pending) {
          initDeliveryRoutes(pending);
        }
      }
    }
  };

  const fetchAll = async () => {
    try {
      await deliveriesActions.fetchAll();
    } catch (e) {
      // TODO: @aki - 에러 처리 (toast 등)
    }
  };

  const fetchCount = async () => {
    try {
      await deliveriesActions.fetchCount();
    } catch (e) {
      // TODO: @aki - 에러 처리 (toast 등)
    }
  };

  const fetchLimitedHoursCount = async () => {
    try {
      await deliveriesActions.fetchLimitedHoursCount();
    } catch (e) {
      // TODO: @aki - 에러 처리 (toast 등)
      // console.log(e);
    }
  };

  const reloadDeliveries = () => {
    handleChangeReload(true);
  };

  // TODO: useRef 로 정의해서 주소값을 이벤트리스너에 넣기
  const receiveMessage = (e) => {
    if (e.data == null || e.data === "") {
      return;
    }

    const messageKey =
      typeof e.data === "string" ? e.data : JSON.stringify(e.data);
    const now = Date.now();
    if (
      messageKey === lastRnMessageRef.current.key &&
      now - lastRnMessageRef.current.time < 80
    ) {
      return;
    }
    lastRnMessageRef.current = { key: messageKey, time: now };

    if (e.data === POSITION_RELOAD) {
      handleChangeReload(true);
    } else if (isFontSize(e.data)) {
      commonActions.setSize(e.data);
    } else {
      let message = e.data;
      if (typeof e.data === "string") {
        try {
          message = JSON.parse(e.data);
        } catch (err) {
          return;
        }
      }
      if (!message || typeof message !== "object") {
        return;
      }

      if (message.type === "favoRiders") {
        ridersActions.setFavoRiders(message.favoRiders);
      } else if (message.type === "deliveryStartBookIds") {
        deliveriesActions.setDeliveryStartBookIds(message.deliveryStartBookIds);

        setReady(true);
      } else if (message.type === "deliveryStartBookIdsObj") {
        deliveriesActions.setDeliveryStartBookIdsObj(
          message.deliveryStartBookIdsObj,
        );

        setReady(true);
      } else if (message.type === "delayed") {
        if (message.action === "prev") {
          handleClickPrevDeliveries({
            force: true,
            selectedDeliveriesWithDelay: message.selectedDeliveriesWithDelay,
          });
        } else if (message.action === "postponed") {
          handleClickPostponedDeliveries({
            force: true,
            selectedDeliveriesWithDelay: message.selectedDeliveriesWithDelay,
          });
        }
      } else if (message.latitude != null && message.longitude != null) {
        mapActions.updateCurrentPostion(message);
      }
    }
  };

  const startNaverMap = () => {
    setMapLoadFailed(false);
    initMap(
      () => {
        mapActions.drawMap({ zoom: 15 });
      },
      () => {
        setMapLoadFailed(true);
      },
    );
  };

  useEffect(() => {
    startNaverMap();

    setTimeout(() => {
      document.addEventListener("message", receiveMessage);
      window.addEventListener("message", receiveMessage);
    }, 200);

    return () => {
      document.removeEventListener("message", receiveMessage);
      window.removeEventListener("message", receiveMessage);

      if (window.naver && window.naver.maps) {
        window.naver.maps.Event.removeListener();
      }
    };
  }, []);

  const [localCurrentCenter, setLocalCurrentCenter] = useState(false);

  // 순서지정 모드 진입 시 토글 초기화
  useEffect(() => {
    if (isOrderMode) {
      setLocalCurrentCenter(false);
    }
  }, [isOrderMode]);

  const handleClickCenterToggle = () => {
    const next = !localCurrentCenter;
    setLocalCurrentCenter(next);
    mapActions.getCurrentPosition(next.toString());
  };

  const isExcludeModeRef = useRef(false);

  const handleClickStartRearrange = ({
    rearrangeDescription,
    onlyDeliveryStart = false,
  }) => {
    isExcludeModeRef.current = onlyDeliveryStart;

    if (onlyDeliveryStart) {
      if (statuses.length === 1 && statuses.includes(STATUS_DELIVERY_STARTED)) {
        handleChangeReload(true);
      } else {
        deliveriesActions.setStatusesOnlyDeliveryStart();
      }
    } else {
      handleChangeReload(true);
    }

    reallocationActions.startRearrangeMode({ rearrangeDescription });
  };

  const handleClickCancelRearrangeMode = () => {
    if (
      window.confirm(
        "배차전환 취소하시겠습니까? 취소 시 현재까지 선택된 마커가 모두 삭제됩니다.",
      )
    ) {
      const wasExcludeMode = isExcludeModeRef.current;
      isExcludeModeRef.current = false;
      setDeliveryRoutesMap({});
      deliveryRoutesMapRef.current = {};
      lastCheckedDeliveryBookIdsRef.current = new Set();
      handleChangeReload(true);
      deliveriesActions.setPreviousStatuses();
      reallocationActions.cancelRearrangeMode();

      if (wasExcludeMode) {
        // 출발건제외 취소 시에도 순서지정 팝업 재표시
        setCheckedDeliveryStartedOrders(false);
        isInitializingRoutes.current = false;
        routeInitRetryCountRef.current = 0;
      }
    }
  };

  const selectDelivery = ({
    bookId,
    status,
    statusString,
    statusRearrangeKey,
    samePlaces,
    reallocatedRiderId,
    type,
    delayedDeliveries,
  }) => {
    if (
      selectedDeliveries.length > 0 &&
      selectedDeliveries[0].status !== status
    ) {
      toast(`${selectedDeliveries[0].statusString}건만 체크해주세요`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
      });
    } else {
      reallocationActions.selectDelivery({
        bookId,
        status,
        statusString,
        statusRearrangeKey,
        samePlaces,
        reallocatedRiderId,
        type,
        delayedDeliveries,
      });
    }
  };

  // 배송순서 지정
  const handleClickStartOrder = () => {
    const hasAssignedSequence = deliveriesStartedWithLocationTracking.some(
      (d) => hasSequenceAssigned(deliveryRoutesMap[bookIdKey(d.bookId)]),
    );

    if (hasAssignedSequence) {
      window.alert(
        "배송순서 재지정은 불가능합니다.\n이번 회차에서 배송수행이 불가능한 접수건은 배차 버튼을 터치하여 연기 상태로 변경해주십시오.",
      );
      return;
    }

    if (!deliveriesStartedWithLocationTracking.length && orderList.length > 0) {
      // 이미 순서 지정이 되어 있을 경우
      openModal(
        <ResetConfirmModal
          key="reset-confirm-modal"
          startOrder={setOrderMode}
        />,
      );
    } else {
      setOrderMode();
    }
  };

  // 순서지정 진입 전 원래 중앙 설정 저장용 ref
  const prevCurrentCenterRef = useRef(null);

  const setOrderMode = () => {
    if (statuses.length === 1 && statuses.includes(STATUS_DELIVERY_STARTED)) {
      handleChangeReload(true);
    } else {
      deliveriesActions.setStatusesOnlyDeliveryStart();
    }

    prevCurrentCenterRef.current = currentCenter;
    mapActions.getCurrentPosition("false");

    orderActions.startOrderMode();
    closeModal();
  };

  const selectOrderDelivery = ({
    bookId,
    status,
    receiverAddress,
    samePlaces,
    latitude,
    longitude,
    corpUserEnableRiderLocationTracking,
  }) => {
    // 위치추적(배송순서 강제지정) 대상건이 하나라도 있으면 그 대상건만 선택 가능.
    // 대상건이 아예 없는 회차(위치추적 미사용 기업회원)라면 배송출발건 전체를 선택 가능하도록 둔다.
    const orderModeRequiresTracking =
      deliveriesStartedWithLocationTracking.length > 0;

    if (
      status === STATUS_DELIVERY_STARTED &&
      (!orderModeRequiresTracking || corpUserEnableRiderLocationTracking)
    ) {
      const alreadySelectedBookIds = new Set(
        newOrderList.map((el) => el.bookId),
      );

      // ✅ 이미 선택된 마커 → 해당 마커만 단건 해제
      if (alreadySelectedBookIds.has(bookId)) {
        orderActions.selectOrderDelivery({
          bookId,
          receiverAddress,
          samePlaces,
        });
        return;
      }

      // 미선택 마커 → 인접 마커 자동 선택
      const nearbyUnselected = markerList
        .filter((d) => {
          if (d.bookId === bookId) return false;
          if (alreadySelectedBookIds.has(d.bookId)) return false;
          if (d.status !== STATUS_DELIVERY_STARTED) return false;
          if (!deliveriesStartedWithLocationTracking.length) return false;
          if (
            orderModeRequiresTracking &&
            !d.corpUserEnableRiderLocationTracking
          )
            return false;

          const dist = calcDistance(
            Number(latitude),
            Number(longitude),
            Number(d.latitude),
            Number(d.longitude),
          );

          return dist <= 70;
        })
        .sort((a, b) => {
          const distA = calcDistance(
            Number(latitude),
            Number(longitude),
            Number(a.latitude),
            Number(a.longitude),
          );
          const distB = calcDistance(
            Number(latitude),
            Number(longitude),
            Number(b.latitude),
            Number(b.longitude),
          );
          return distA - distB;
        });

      const toSelect = [
        { bookId, receiverAddress, samePlaces },
        ...nearbyUnselected.map((d) => ({
          bookId: d.bookId,
          receiverAddress: d.receiverAddress || d.receiverAddressRoad || "",
          samePlaces: d.samePlaces,
        })),
      ];

      orderActions.selectOrderDeliveries(toSelect);

      if (nearbyUnselected.length > 0) {
        toast(
          `인접 마커 ${nearbyUnselected.length}건이 자동으로 순서 지정되었습니다.`,
          {
            className: "toast-background",
            bodyClassName: "toast-body",
            progressClassName: "fancy-progress-bar",
            autoClose: 2000,
          },
        );
      }
    } else {
      toast(`배송 건만 체크해주세요`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
      });
    }
  };
  const handleClickCancelOrderMode = () => {
    if (
      window.confirm(
        "순서지정을 취소하시겠습니까? 취소 시 현재까지 지정한 순서가 모두 삭제됩니다.",
      )
    ) {
      cancelOrderMode({ resetStatuses: true });
    }
  };

  // 순서지정 모드 종료 시 — 원래 설정 복원
  const cancelOrderMode = ({ resetStatuses = true }) => {
    orderActions.cancelOrderMode();
    closeModal();

    if (resetStatuses) {
      deliveriesActions.setPreviousStatuses();
    }

    // ✅ 저장해둔 원래 중앙 설정 복원
    if (prevCurrentCenterRef.current !== null) {
      mapActions.getCurrentPosition(prevCurrentCenterRef.current.toString());
      prevCurrentCenterRef.current = null;
    }

    handleChangeReload(true);
  };

  // 마커 클릭 핸들러
  const getClickHandler = (seq) => {
    return async function () {
      var marker = markers[seq],
        infoWindow = infoWindows[seq];

      const markerDelivery = marker.delivery;

      //  폴리라인 삭제
      for (let i = 0; i < polylines.length; i++) {
        polylines[i].setMap(null);
      }

      // 건물입구점 폴리라인 삭제
      for (let i = 0; i < polylinesBaseAddressEntryPoint.length; i++) {
        polylinesBaseAddressEntryPoint[i].setMap(null);
      }

      // 수지원 마커 삭제
      for (let i = 0; i < markersSoojione.length; i++) {
        markersSoojione[i].setMap(null);
      }

      // 건물입구점 마커 삭제
      for (let i = 0; i < markersBaseAddressEntryPoint.length; i++) {
        markersBaseAddressEntryPoint[i].setMap(null);
      }

      if (infoWindow.getMap()) {
        infoWindow.close();
      } else {
        if (isOrderMode) {
          selectOrderDelivery({
            bookId: marker.bookId,
            status: marker.status,
            receiverAddress:
              marker.receiverAddress || marker.receiverAddressRoad || "",
            samePlaces: marker.samePlaces,
            latitude: marker.latitude,
            longitude: marker.longitude,
            corpUserEnableRiderLocationTracking:
              marker.corpUserEnableRiderLocationTracking,
          });
        } else if (isRearrangeMode) {
          selectDelivery({
            bookId: marker.bookId,
            status: marker.status,
            statusString: marker.statusString,
            statusRearrangeKey: marker.statusRearrangeKey,
            samePlaces: marker.samePlaces,
            reallocatedRiderId: marker.reallocatedRiderId,
            type: marker.delivery?.type,
            delayedDeliveries: marker.delivery?.delayedDeliveries,
          });
        } else {
          // let markerDeliveries = [];
          // if (marker.delivery?.samePlaces) {
          //   markerDeliveries.push(marker.delivery);
          // }
          // if (marker.delivery?.samePlaces) {
          //   markerDeliveries = [marker.delivery, ...marker.delivery.samePlaces];
          // } else if (marker.delivery) {
          //   markerDeliveries = [marker.delivery];
          // }

          // const uniqueQueries = new Set();
          // let queries = [];
          // let promisesCses = [];
          // let promisesReviews = [];
          // let promisesFrontdoorPasswords = [];

          // markerDeliveries.map(async (d) => {
          //   let query = {};

          //   if (d.status === STATUS_PICKUP) {
          //     query = {
          //       addressRoad: d.senderAddressRoad,
          //     };

          //     if (d.senderBuildingDongNumber) {
          //       query.buildingDongNumber = d.senderBuildingDongNumber;
          //     }
          //     if (d.senderBuildingHouseNumber) {
          //       query.buildingHouseNumber = d.senderBuildingHouseNumber;
          //     }
          //   } else {
          //     query = {
          //       addressRoad: d.receiverAddressRoad,
          //     };

          //     if (d.receiverBuildingDongNumber) {
          //       query.buildingDongNumber = d.receiverBuildingDongNumber;
          //     }
          //     if (d.receiverBuildingHouseNumber) {
          //       query.buildingHouseNumber = d.receiverBuildingHouseNumber;
          //     }
          //   }

          //   const queryKey = JSON.stringify(query);

          //   if (!uniqueQueries.has(queryKey)) {
          //     uniqueQueries.add(queryKey);
          //     queries.push(query);
          //     promisesCses.push(deliveriesActions.fetchCsesByAddress(query));
          //     promisesReviews.push(
          //       deliveriesActions.fetchReviewsByAddress(query),
          //     );
          //     promisesFrontdoorPasswords.push(
          //       deliveriesActions.fetchFrontdoorPasswordsByAddress(query),
          //     );
          //   }
          // });

          // const csesByAddress = await Promise.allSettled(promisesCses);
          // const reviewsByAddress = await Promise.allSettled(promisesReviews);
          // const frontdoorPasswordsByAddress = await Promise.allSettled(
          //   promisesFrontdoorPasswords,
          // );

          // const queryForBaseAddressEntryPoints = {
          //   page: 1,
          //   address:
          //     marker.delivery?.status === STATUS_PICKUP
          //       ? marker.delivery?.senderAddressRoad
          //       : marker.delivery?.receiverAddressRoad,
          // };

          // if (marker.delivery.status === STATUS_PICKUP) {
          //   if (marker.delivery?.senderBuilding) {
          //     queryForBaseAddressEntryPoints.building =
          //       marker.delivery?.senderBuilding;
          //   }
          // } else {
          //   if (marker.delivery?.receiverBuilding) {
          //     queryForBaseAddressEntryPoints.building =
          //       marker.delivery?.receiverBuilding;
          //   }
          // }

          // const baseAddressEntryPoints =
          //   await deliveriesActions.fetchBaseAddressEntryPoints(
          //     queryForBaseAddressEntryPoints,
          //   );

          let infoCses = [];
          // csesByAddress.forEach((ca, idx) => {
          //   if (ca.status === "fulfilled" && ca.value.rows?.length > 0) {
          //     const cs = ca.value.rows.find(
          //       (el) => !!el._category && el._cateogry !== 5,
          //     );

          //     if (cs) {
          //       infoCses.push({
          //         ...cs,
          //         buildingDongNumber: queries[idx].buildingDongNumber,
          //         buildingHouseNumber: queries[idx].buildingHouseNumber,
          //       });
          //     }
          //   }
          // });

          let infoReviews = [];
          // reviewsByAddress.forEach((ra, idx) => {
          //   if (ra.status === "fulfilled" && ra.value.rows?.length > 0) {
          //     const review = ra.value.rows.find(
          //       (el) => !!el._category && el._cateogry !== 7,
          //     );

          //     if (review) {
          //       infoReviews.push({
          //         ...review,
          //         buildingDongNumber: queries[idx].buildingDongNumber,
          //         buildingHouseNumber: queries[idx].buildingHouseNumber,
          //       });
          //     }
          //   }
          // });

          let infoFrontDoorPasswords = [];
          // frontdoorPasswordsByAddress.forEach((ra) => {
          //   if (ra.status === "fulfilled" && ra.value.length > 0) {
          //     infoFrontDoorPasswords.push({
          //       ...ra.value[0],
          //     });
          //   }
          // });

          /**
           * 툴팁 그리기
           */
          // 수거일경우 동일 주소 툴팁 표시 제외
          let pickupInfoWindowDone = false;

          // 동일수 하인 동일 주소의 개수
          let receiverWaitDeliveriesArr =
            markerDelivery.sameReceiverDeliveriesObj.waiting || [];
          let receiverStartedDeliveriesArr =
            markerDelivery.sameReceiverDeliveriesObj.started || [];
          let receiverPostponedDeliveriesArr =
            markerDelivery.sameReceiverDeliveriesObj.postponed || [];

          let titleAddressRoad = "";
          if (
            (markerDelivery.status === STATUS_PICKUP ||
              markerDelivery.statusAddressNotSupported === STATUS_PICKUP) &&
            markerDelivery.senderAddressRoadWithoutSigungu
          ) {
            titleAddressRoad =
              markerDelivery.senderAddressRoadWithoutSigungu.replace(/ /g, "");
          } else if (
            (markerDelivery.status === STATUS_PICKUP ||
              markerDelivery.statusAddressNotSupported === STATUS_PICKUP) &&
            markerDelivery.senderAddressWithoutSigungu
          ) {
            titleAddressRoad =
              markerDelivery.senderAddressWithoutSigungu.replace(/ /g, "");
          } else if (
            markerDelivery.status !== STATUS_PICKUP &&
            markerDelivery.statusAddressNotSupported !== STATUS_PICKUP &&
            markerDelivery.receiverAddressRoadWithoutSigungu
          ) {
            titleAddressRoad =
              markerDelivery.receiverAddressRoadWithoutSigungu.replace(
                / /g,
                "",
              );
          } else if (
            markerDelivery.status !== STATUS_PICKUP &&
            markerDelivery.statusAddressNotSupported !== STATUS_PICKUP &&
            markerDelivery.receiverAddressWithoutSigungu
          ) {
            titleAddressRoad =
              markerDelivery.receiverAddressWithoutSigungu.replace(/ /g, "");
          }

          let linkingOtherApp = `window.__markMapCameraRestore&&window.__markMapCameraRestore();window.ReactNativeWebView.postMessage('${JSON.stringify(
            {
              type: "linking",
              app: linkOthers,
              longitude: markerDelivery.longitude,
              latitude: markerDelivery.latitude,
              title: `${
                markerDelivery.order ? `(${markerDelivery.order})` : ""
              }${titleAddressRoad}`,
            },
          )}');`;

          let linkingOtherAppRoute = `window.__markMapCameraRestore&&window.__markMapCameraRestore();window.ReactNativeWebView.postMessage('${JSON.stringify(
            {
              type: "route",
              app: linkOthers,
              longitude: markerDelivery.longitude,
              latitude: markerDelivery.latitude,
              title:
                (markerDelivery.renderSenderName
                  ? markerDelivery.renderSenderName
                      .replace(/\//g, "")
                      .replace(/ /g, "")
                  : "") +
                (markerDelivery.renderReceiverName
                  ? markerDelivery.renderReceiverName
                      .replace(/\//g, "")
                      .replace(/ /g, "")
                  : ""),
            },
          )}');`;

          // 상세 페이지 이동
          let linkingDetail = `window.__markMapCameraRestore&&window.__markMapCameraRestore();window.ReactNativeWebView.postMessage('${JSON.stringify(
            {
              type: "detail",
              bookId: markerDelivery.bookId,
            },
          )}');`;

          if (!appVersion) {
            linkingDetail = `window.__markMapCameraRestore&&window.__markMapCameraRestore();window.ReactNativeWebView.postMessage('${markerDelivery.bookId}');`;
          }

          let infoWindowContent = ``;

          let orderingNumberByDeliveryRiderId = "";
          // let isUploadedOrderingNumber = true;

          if (markerDelivery.orderingNumberByDeliveryRiderId) {
            orderingNumberByDeliveryRiderId = ` [${markerDelivery.orderingNumberByDeliveryRiderId}]`;

            // if (!markerDelivery.isUploadedOrderingNumber) {
            //   isUploadedOrderingNumber = false;
            // }
          }

          if (appVersion) {
            if (linkOthers === TMAP) {
              infoWindowContent += `
                <div class="info-button-wrap">
                  <button type="button" class="btn" onclick="${linkingOtherAppRoute.replace(/"/g, "&quot;")}">경로 찾기</button>
                </div>
              `;
            } else {
              infoWindowContent += `
              <div class="info-button-wrap">
                <button type="button" class="btn btn-border" onclick="${linkingOtherApp.replace(/"/g, "&quot;")}">위치 찾기</button>
                <button type="button" class="btn" onclick="${linkingOtherAppRoute.replace(/"/g, "&quot;")}">경로 찾기</button>
              </div>
            `;
            }
          }

          infoWindowContent += `
            <div style="max-width: 250px; overflow-x: clip;">
            <div style="max-height: 278px; padding-bottom: 40px; overflow-y: auto; overflow-x: visible;">
              <div 
                class="info ${markerDelivery.statusText} ${markerDelivery.keyColor}" 
                style="padding: 10px; max-width: 250px;" 
                onclick=${linkingDetail}
              >
              <div class="sub-info">
            `;

          if (infoFrontDoorPasswords.length > 0) {
            const buildingDongNumber =
              markerDelivery.status === STATUS_PICKUP
                ? markerDelivery.senderBuildingDongNumber
                : markerDelivery.receiverBuildingDongNumber;
            const buildingHouseNumber =
              markerDelivery.status === STATUS_PICKUP
                ? markerDelivery.senderBuildingHouseNumber?.slice(-2)
                : markerDelivery.receiverBuildingHouseNumber?.slice(-2);

            const infoFrontDoorPassword = infoFrontDoorPasswords.find(
              (e) =>
                ((!e.buildingDongNumber && !buildingDongNumber) ||
                  Number(e.buildingDongNumber) ===
                    Number(buildingDongNumber)) &&
                ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                  Number(e.buildingHouseNumber) ===
                    Number(buildingHouseNumber)),
            );

            if (infoFrontDoorPassword) {
              infoWindowContent += `<div class="sub-info-box sub-info-password">${infoFrontDoorPassword.password}</div>`;
            }
          }

          if (infoCses.length > 0) {
            const buildingDongNumber =
              markerDelivery.status === STATUS_PICKUP
                ? markerDelivery.senderBuildingDongNumber
                : markerDelivery.receiverBuildingDongNumber;
            const buildingHouseNumber =
              markerDelivery.status === STATUS_PICKUP
                ? markerDelivery.senderBuildingHouseNumber
                : markerDelivery.receiverBuildingHouseNumber;

            const infoCs = infoCses.find(
              (e) =>
                ((!e.buildingDongNumber && !buildingDongNumber) ||
                  Number(e.buildingDongNumber) ===
                    Number(buildingDongNumber)) &&
                ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                  Number(e.buildingHouseNumber) ===
                    Number(buildingHouseNumber)),
            );

            if (infoCs) {
              infoWindowContent += `<div class="sub-info-box sub-info-black">주의: ${renderCsCategory(
                infoCs._category,
              )}</div>`;
            }
          }

          if (infoReviews.length > 0) {
            const buildingDongNumber =
              markerDelivery.status === STATUS_PICKUP
                ? markerDelivery.senderBuildingDongNumber
                : markerDelivery.receiverBuildingDongNumber;
            const buildingHouseNumber =
              markerDelivery.status === STATUS_PICKUP
                ? markerDelivery.senderBuildingHouseNumber
                : markerDelivery.receiverBuildingHouseNumber;

            const infoReview = infoReviews.find(
              (e) =>
                ((!e.buildingDongNumber && !buildingDongNumber) ||
                  Number(e.buildingDongNumber) ===
                    Number(buildingDongNumber)) &&
                ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                  Number(e.buildingHouseNumber) ===
                    Number(buildingHouseNumber)),
            );

            if (infoReview) {
              infoWindowContent += `<div class="sub-info-box sub-info-black">주의: ${renderReviewCategory(
                infoReview._category,
              )}</div>`;
            }
          }

          infoWindowContent += `
              </div>
                ${
                  markerDelivery.status === STATUS_PICKUP ||
                  markerDelivery.statusAddressNotSupported === STATUS_PICKUP
                    ? ""
                    : `
                  <div class="info-icons">
                    <div class="info-icon highPrice ${
                      !markerDelivery.isHighPrice && "disabled"
                    }">
                      <img style="width: 6px;" src="${mapImages.iconDollar}" />
                    </div>
                    <div class="info-icon time ${
                      !markerDelivery.isLimitedHours && "disabled"
                    }">
                      <img style="width: 6px;" src="${mapImages.iconClock}" />
                    </div>
                    <div class="info-icon rearrange ${
                      !markerDelivery.reallocated && "disabled"
                    }">
                      <img style="width: 8px;" src="${mapImages.iconRotation}" />
                    </div>
                    <div class="info-icon deliveryWait ${
                      receiverWaitDeliveriesArr.length === 0 && "disabled"
                    }">
                      ${receiverWaitDeliveriesArr.length}
                    </div>
                    <div class="info-icon deliveryStart ${
                      receiverStartedDeliveriesArr.length === 0 && "disabled"
                    }">
                      ${receiverStartedDeliveriesArr.length}
                    </div>
                    <div class="info-icon deliveryPostponed ${
                      receiverPostponedDeliveriesArr.length === 0 && "disabled"
                    }">
                      <img style="width: 7px;" src="${mapImages.iconPostponed}" />
                    </div>
                    <div class="info-icon translated ${
                      !markerDelivery.isTranslated && "disabled"
                    }">
                      <img style="width: 7px;" src="${mapImages.iconTranslated}" />
                    </div>
                  </div>
                `
                }
                <div class="info-title ${markerDelivery.statusText}">
                  <span style="color: ${markerDelivery.keyColorHex}">
                    (${markerDelivery.statusString})
                  </span><span style="${orderingNumberColor === "red" ? "color: red" : ""}">${orderingNumberByDeliveryRiderId}</span>&nbsp;
                  ${markerDelivery.bookId}&nbsp;
                  <span style="color: ${markerDelivery.keyColorHex}">
                    ${markerDelivery.typeString}
                  </span>
                </div>
                <div class="info-spot">
                  ${
                    markerDelivery.status === STATUS_PICKUP ||
                    markerDelivery.statusAddressNotSupported === STATUS_PICKUP
                      ? `${markerDelivery.spotName} ${
                          markerDelivery.spot
                            ? `(${markerDelivery.renderSenderName})`
                            : ""
                        }`
                      : `${markerDelivery.renderReceiverName}(${markerDelivery.renderSenderName})`
                  }
                  <span class="c-red">
                    ${
                      markerDelivery.status !== STATUS_PICKUP &&
                      markerDelivery.isTranslatedWithNoReceiverPoint
                        ? " (번역주의)"
                        : ""
                    }
                    ${
                      markerDelivery.status !== STATUS_PICKUP &&
                      !markerDelivery.isTranslatedWithNoReceiverPoint &&
                      markerDelivery.isTranslated
                        ? " (영어주소)"
                        : ""
                    }
                  </span>
                </div>
                <div class="info-base-address">
                ${
                  markerDelivery.type === DELIVERY_TYPE_NORMAL
                    ? `${
                        markerDelivery.displayReceiverBuildingDongHouseNumber ||
                        "없음"
                      }(${markerDelivery.renderAddressApiType})`
                    : markerDelivery.type !== DELIVERY_TYPE_NORMAL
                      ? `${
                          markerDelivery.displaySenderBuildingDongHouseNumber ||
                          "없음"
                        }(${markerDelivery.renderAddressApiType})`
                      : ""
                } 
                ${
                  markerDelivery.frontdoorPassword
                    ? ` / ${markerDelivery.frontdoorPassword}`
                    : ""
                }        
                </div>
                
                ${
                  markerDelivery.renderRequestedDeliveryTime ||
                  markerDelivery.renderLimitedDeliveryAt
                    ? `<div class="info-reqeusted-time">${
                        markerDelivery.renderRequestedDeliveryTime ||
                        markerDelivery.renderLimitedDeliveryAt ||
                        ""
                      }</div>`
                    : ""
                }
                
                <div class="info-address">
                  ${
                    markerDelivery.status === STATUS_PICKUP ||
                    markerDelivery.statusAddressNotSupported === STATUS_PICKUP
                      ? `${
                          markerDelivery.deliveryPlace &&
                          (markerDelivery.type === DELIVERY_TYPE_PICKUP ||
                            markerDelivery.type === DELIVERY_TYPE_RETURN)
                            ? `<span class="bg-red">보관등록</span> `
                            : ""
                        }(출)${
                          markerDelivery.renderRefinedAddressRawResultError
                            ? `<span class="c-red">(${markerDelivery.renderRefinedAddressRawResultError})</span>`
                            : ""
                        } ${
                          markerDelivery.senderAddressRoad ||
                          markerDelivery.senderAddress ||
                          ""
                        }, ${markerDelivery.senderAddressDetail}`
                      : `${
                          markerDelivery.soojioneLimitedCustomized
                            ? `<span class="bg-red">주의${markerDelivery.distanceRefineToLimitedRefined}</span> `
                            : ""
                        }${
                          markerDelivery.spotKurlyDrink ||
                          markerDelivery.mustSignature
                            ? `<span class="bg-red">서명</span> `
                            : ""
                        }(도)
                        ${
                          markerDelivery.renderRefinedAddressRawResultError
                            ? `<span class="c-red">(${markerDelivery.renderRefinedAddressRawResultError})</span>`
                            : ""
                        } ${
                          markerDelivery.status === STATUS_PICKUP ||
                          markerDelivery.statusAddressNotSupported ===
                            STATUS_PICKUP
                            ? `${
                                markerDelivery.senderAddressRoadWithoutSigungu ||
                                markerDelivery.senderAddressWithoutSigungu
                              } ${
                                markerDelivery.senderAddressObj &&
                                markerDelivery.senderAddressObj.building
                                  ? ` ${markerDelivery.senderAddressObj.building}`
                                  : ""
                              }`
                            : `${
                                markerDelivery.receiverAddressRoadWithoutSigungu ||
                                markerDelivery.receiverAddressWithoutSigungu
                              }${
                                markerDelivery.receiverAddressObj &&
                                markerDelivery.receiverAddressObj.building
                                  ? ` ${markerDelivery.receiverAddressObj.building}`
                                  : ""
                              }
                        ${
                          !markerDelivery.receiverAddressRoadWithoutSigungu &&
                          markerDelivery.receiverDong
                            ? `/${markerDelivery.receiverDong}`
                            : ""
                        }`
                        } ${
                          markerDelivery.memoFromCustomer
                            ? `<span class="c-red">(${markerDelivery.memoFromCustomer})</span>`
                            : ""
                        }`
                  }
                </div>
                <div class="info-address bold">
                  ${
                    markerDelivery.status !== STATUS_PICKUP &&
                    markerDelivery.statusAddressNotSupported !==
                      STATUS_PICKUP &&
                    markerDelivery.customerAddressEngGpt
                      ? `(GPT번역) ${markerDelivery.customerAddressEngGpt}`
                      : ""
                  }
                </div>
                <div class="info-address">
                  ${markerDelivery.displayAddress}
                </div>
                <div class="info-address bold">
                  ${
                    markerDelivery.status !== STATUS_PICKUP &&
                    markerDelivery.isTranslatedWithNoReceiverPoint
                      ? `(번역주의) ${markerDelivery.originAddressEng}`
                      : ""
                  }
                </div>
              </div>
          `;

          if (
            (markerDelivery.status === STATUS_PICKUP ||
              markerDelivery.statusAddressNotSupported === STATUS_PICKUP) &&
            markerDelivery.type === DELIVERY_TYPE_NORMAL
          ) {
            pickupInfoWindowDone = true;
          }

          // 툴팁 : 동일 주소 목록
          // 배송출발 마커에 동일 위치 연기건 (+)가 있으면, 그 연기건도 함께 표시
          const tooltipSamePlaces = [...(markerDelivery.samePlaces || [])];

          if (
            markerDelivery.status === STATUS_DELIVERY_STARTED ||
            markerDelivery.statusAddressNotSupported === STATUS_DELIVERY_STARTED
          ) {
            const { nearbyMarkersInOtherStatusArr } = checkNearbyMarkers(
              markerDelivery,
              markerListWithSameplace,
            );

            nearbyMarkersInOtherStatusArr.forEach((nearby) => {
              if (nearby.status !== STATUS_DELIVERY_POSTPONED) return;

              [nearby, ...(nearby.samePlaces || [])].forEach((postponed) => {
                if (postponed.status !== STATUS_DELIVERY_POSTPONED) return;
                if (postponed.bookId === markerDelivery.bookId) return;
                if (
                  tooltipSamePlaces.some((s) => s.bookId === postponed.bookId)
                ) {
                  return;
                }
                tooltipSamePlaces.push(postponed);
              });
            });
          }

          if (tooltipSamePlaces.length > 0) {
            tooltipSamePlaces.forEach((same) => {
              if (
                same.status === STATUS_DELIVERY_WAITING ||
                same.status === STATUS_DELIVERY_POSTPONED ||
                same.status === STATUS_DELIVERY_STARTED ||
                same.statusAddressNotSupported === STATUS_DELIVERY_STARTED ||
                same.type === DELIVERY_TYPE_PICKUP ||
                same.type === DELIVERY_TYPE_RETURN ||
                !pickupInfoWindowDone
              ) {
                let linkingDetail = `window.__markMapCameraRestore&&window.__markMapCameraRestore();window.ReactNativeWebView.postMessage('${JSON.stringify(
                  {
                    type: "detail",
                    bookId: same.bookId,
                  },
                )}');`;

                if (!appVersion) {
                  linkingDetail = `window.__markMapCameraRestore&&window.__markMapCameraRestore();window.ReactNativeWebView.postMessage('${same.bookId}');`;
                }

                let sameReceiverWaitDeliveriesArr =
                  same.sameReceiverDeliveriesObj?.waiting || [];
                let sameReceiverStartedDeliveriesArr =
                  same.sameReceiverDeliveriesObj?.started || [];
                let sameReceiverPostponedDeliveriesArr =
                  same.sameReceiverDeliveriesObj?.postponed || [];

                let sameOrderingNumberByDeliveryRiderId = "";
                // let sameIsUploadedOrderingNumber = true;

                if (same.orderingNumberByDeliveryRiderId) {
                  sameOrderingNumberByDeliveryRiderId = ` [${same.orderingNumberByDeliveryRiderId}]`;

                  // if (!same.isUploadedOrderingNumber) {
                  //   sameIsUploadedOrderingNumber = false;
                  // }
                }

                infoWindowContent += `<div 
                    class="info ${same.statusText} ${same.keyColor}" 
                    style="padding: 5px 10px 10px; max-width: 250px; position: relative;" 
                    onclick=${linkingDetail}
                  >
                  <div class="sub-info">`;

                if (infoFrontDoorPasswords.length > 0) {
                  const buildingDongNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingDongNumber
                      : same.receiverBuildingDongNumber;
                  const buildingHouseNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingHouseNumber?.slice(-2)
                      : same.receiverBuildingHouseNumber?.slice(-2);

                  const infoFrontDoorPassword = infoFrontDoorPasswords.find(
                    (e) =>
                      ((!e.buildingDongNumber && !buildingDongNumber) ||
                        Number(e.buildingDongNumber) ===
                          Number(buildingDongNumber)) &&
                      ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                        Number(e.buildingHouseNumber) ===
                          Number(buildingHouseNumber)),
                  );

                  if (infoFrontDoorPassword) {
                    infoWindowContent += `<div class="sub-info-box sub-info-password">${infoFrontDoorPassword.password}</div>`;
                  }
                }

                if (infoCses.length > 0) {
                  const buildingDongNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingDongNumber
                      : same.receiverBuildingDongNumber;
                  const buildingHouseNumber =
                    markerDelivery.status === STATUS_PICKUP
                      ? same.senderBuildingHouseNumber
                      : same.receiverBuildingHouseNumber;

                  const infoCs = infoCses.find(
                    (e) =>
                      ((!e.buildingDongNumber && !buildingDongNumber) ||
                        Number(e.buildingDongNumber) ===
                          Number(buildingDongNumber)) &&
                      ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                        Number(e.buildingHouseNumber) ===
                          Number(buildingHouseNumber)),
                  );

                  if (infoCs) {
                    infoWindowContent += `<div class="sub-info-box sub-info-black">주의: ${renderCsCategory(
                      infoCs._category,
                    )}</div>`;
                  }
                }

                if (infoReviews.length > 0) {
                  const buildingDongNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingDongNumber
                      : same.receiverBuildingDongNumber;
                  const buildingHouseNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingHouseNumber
                      : same.receiverBuildingHouseNumber;

                  const infoReview = infoReviews.find(
                    (e) =>
                      ((!e.buildingDongNumber && !buildingDongNumber) ||
                        Number(e.buildingDongNumber) ===
                          Number(buildingDongNumber)) &&
                      ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                        Number(e.buildingHouseNumber) ===
                          Number(buildingHouseNumber)),
                  );

                  if (infoReview) {
                    infoWindowContent += `<div class="sub-info-box sub-info-black">주의: ${renderReviewCategory(
                      infoReview._category,
                    )}</div>`;
                  }
                }

                infoWindowContent += `
                  </div>
                    ${
                      same.status === STATUS_PICKUP ||
                      same.statusAddressNotSupported === STATUS_PICKUP
                        ? ""
                        : `
                      <div class="info-icons">
                        <div class="info-icon highPrice ${
                          !same.isHighPrice && "disabled"
                        }">
                          <img style="width: 6px;" src="${mapImages.iconDollar}" />
                        </div>
                        <div class="info-icon time ${
                          !same.isLimitedHours && "disabled"
                        }">
                          <img style="width: 6px;" src="${mapImages.iconClock}" />
                        </div>
                        <div class="info-icon rearrange ${
                          !same.reallocated && "disabled"
                        }">
                          <img style="width: 8px;" src="${mapImages.iconRotation}" />
                        </div>
                        <div class="info-icon deliveryWait ${
                          sameReceiverWaitDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          ${sameReceiverWaitDeliveriesArr.length}
                        </div>
                        <div class="info-icon deliveryStart ${
                          sameReceiverStartedDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          ${sameReceiverStartedDeliveriesArr.length}
                        </div>
                        <div class="info-icon deliveryPostponed ${
                          sameReceiverPostponedDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          <img style="width: 7px;" src="${mapImages.iconPostponed}" />
                        </div>
                        <div class="info-icon translated ${
                          !same.isTranslated && "disabled"
                        }">
                          <img style="width: 7px;" src="${mapImages.iconTranslated}" />
                        </div>
                      </div>
                    `
                    }
                    <div class="info-title ${same.statusText}">
                      <span style="color: ${same.keyColorHex}">(${
                        same.statusString
                      })</span>
                      <span style="${orderingNumberColor === "red" ? "color: red" : ""}">${sameOrderingNumberByDeliveryRiderId}</span>&nbsp;
                      ${same.bookId}&nbsp;
                      <span style="color: ${same.keyColorHex}">${
                        same.typeString
                      }</span>
                    </div>
                    <div class="info-spot">
                      ${
                        same.status === STATUS_PICKUP ||
                        same.statusAddressNotSupported === STATUS_PICKUP
                          ? `${same.spotName} ${
                              same.spot ? `(${same.renderSenderName})` : ""
                            }`
                          : `${same.renderReceiverName}(${same.renderSenderName})`
                      }
                      <span class="c-red">
                        ${
                          same.status !== STATUS_PICKUP &&
                          same.statusAddressNotSupported !== STATUS_PICKUP &&
                          same.isTranslatedWithNoReceiverPoint
                            ? " (번역주의)"
                            : ""
                        }
                        ${
                          same.status !== STATUS_PICKUP &&
                          same.statusAddressNotSupported !== STATUS_PICKUP &&
                          !same.isTranslatedWithNoReceiverPoint &&
                          same.isTranslated
                            ? " (영어주소)"
                            : ""
                        }
                      </span>
                    </div>
                    <div class="info-base-address">
                      ${
                        same.type === DELIVERY_TYPE_NORMAL
                          ? `${
                              same.displayReceiverBuildingDongHouseNumber ||
                              "없음"
                            }(${same.renderAddressApiType})`
                          : same.type !== DELIVERY_TYPE_NORMAL
                            ? `${
                                same.displaySenderBuildingDongHouseNumber ||
                                "없음"
                              }(${same.renderAddressApiType})`
                            : ""
                      } 
                      ${
                        same.frontdoorPassword
                          ? ` / ${same.frontdoorPassword}`
                          : ""
                      }        
                    </div>
                       ${
                         same.renderRequestedDeliveryTime ||
                         same.renderLimitedDeliveryAt
                           ? `<div class="info-reqeusted-time">${
                               same.renderRequestedDeliveryTime ||
                               same.renderLimitedDeliveryAt ||
                               ""
                             }</div>`
                           : ""
                       }
                    <div class="info-address">
                      ${
                        same.status === STATUS_PICKUP ||
                        same.statusAddressNotSupported === STATUS_PICKUP
                          ? `${
                              same.deliveryPlace &&
                              (same.type === DELIVERY_TYPE_PICKUP ||
                                same.type === DELIVERY_TYPE_RETURN)
                                ? `<span class="bg-red">보관등록</span> `
                                : ""
                            }(출)${
                              same.renderRefinedAddressRawResultError
                                ? `<span class="c-red">(${same.renderRefinedAddressRawResultError})</span>`
                                : ""
                            } ${
                              same.senderAddressRoad || same.senderAddress || ""
                            }, ${same.senderAddressDetail}`
                          : `${
                              same.soojioneLimitedCustomized
                                ? `<span class="bg-red">주의${same.distanceRefineToLimitedRefined}</span> `
                                : ""
                            }${
                              same.spotKurlyDrink || same.mustSignature
                                ? `<span class="bg-red">서명</span> `
                                : ""
                            }(도)${
                              same.renderRefinedAddressRawResultError
                                ? `<span class="c-red">(${same.renderRefinedAddressRawResultError})</span>`
                                : ""
                            } ${
                              same.status === STATUS_PICKUP ||
                              same.statusAddressNotSupported === STATUS_PICKUP
                                ? `${
                                    same.senderAddressRoadWithoutSigungu ||
                                    same.senderAddressWithoutSigungu
                                  } ${
                                    same.senderAddressObj &&
                                    same.senderAddressObj.building
                                      ? ` ${same.senderAddressObj.building}`
                                      : ""
                                  }`
                                : `${
                                    same.receiverAddressRoadWithoutSigungu ||
                                    same.receiverAddressWithoutSigungu
                                  }${
                                    same.receiverAddressObj &&
                                    same.receiverAddressObj.building
                                      ? ` ${same.receiverAddressObj.building}`
                                      : ""
                                  }${
                                    !same.receiverAddressRoadWithoutSigungu &&
                                    same.receiverDong
                                      ? `/${same.receiverDong}`
                                      : ""
                                  }`
                            } ${
                              same.memoFromCustomer
                                ? `<span class="c-red">(${same.memoFromCustomer})</span>`
                                : ""
                            }`
                      }
                    </div>
                    <div class="info-address bold">
                      ${
                        same.status !== STATUS_PICKUP &&
                        same.statusAddressNotSupported !== STATUS_PICKUP &&
                        same.customerAddressEngGpt
                          ? `(GPT번역) ${same.customerAddressEngGpt}`
                          : ""
                      }
                    </div>
                    <div class="info-address">
                      ${same.displayAddress}
                    </div>
                    <div class="info-address bold">
                      ${
                        same.status !== STATUS_PICKUP &&
                        same.statusAddressNotSupported !== STATUS_PICKUP &&
                        same.isTranslatedWithNoReceiverPoint
                          ? `(번역주의) ${same.originAddressEng}`
                          : ""
                      }
                    </div>
                  </div>
                `;

                if (
                  (same.status === STATUS_PICKUP ||
                    same.statusAddressNotSupported === STATUS_PICKUP) &&
                  same.type === DELIVERY_TYPE_NORMAL
                ) {
                  pickupInfoWindowDone = true;
                }
              }
            });
          }

          // 툴팁 : 동일 주소 목록(배송건) - CS만 표시됨
          if (
            markerDelivery.samePlacesDelivery &&
            markerDelivery.samePlacesDelivery.length > 0
          ) {
            markerDelivery.samePlacesDelivery.forEach((same) => {
              if (
                same.status === STATUS_DELIVERY_WAITING ||
                same.status === STATUS_DELIVERY_POSTPONED ||
                same.status === STATUS_DELIVERY_STARTED ||
                same.statusAddressNotSupported === STATUS_DELIVERY_STARTED ||
                same.type === DELIVERY_TYPE_PICKUP ||
                same.type === DELIVERY_TYPE_RETURN ||
                !pickupInfoWindowDone
              ) {
                let linkingDetail = `window.__markMapCameraRestore&&window.__markMapCameraRestore();window.ReactNativeWebView.postMessage('${JSON.stringify(
                  {
                    type: "detail",
                    bookId: same.bookId,
                  },
                )}');`;

                if (!appVersion) {
                  linkingDetail = `window.__markMapCameraRestore&&window.__markMapCameraRestore();window.ReactNativeWebView.postMessage('${same.bookId}');`;
                }

                let sameReceiverWaitDeliveriesArr =
                  same.sameReceiverDeliveriesObj?.waiting || [];
                let sameReceiverStartedDeliveriesArr =
                  same.sameReceiverDeliveriesObj?.started || [];
                let sameReceiverPostponedDeliveriesArr =
                  same.sameReceiverDeliveriesObj?.postponed || [];

                let sameOrderingNumberByDeliveryRiderId = "";
                // let sameIsUploadedOrderingNumber = true;

                if (same.orderingNumberByDeliveryRiderId) {
                  sameOrderingNumberByDeliveryRiderId = ` [${same.orderingNumberByDeliveryRiderId}]`;

                  // if (!same.isUploadedOrderingNumber) {
                  //   sameIsUploadedOrderingNumber = false;
                  // }
                }

                infoWindowContent += `<div 
                    class="info ${same.statusText} ${same.keyColor}" 
                    style="padding: 5px 10px 10px; max-width: 250px; position: relative;" 
                    onclick=${linkingDetail}
                  >
                  <div class="sub-info">`;

                if (infoFrontDoorPasswords.length > 0) {
                  const buildingDongNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingDongNumber
                      : same.receiverBuildingDongNumber;
                  const buildingHouseNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingHouseNumber?.slice(-2)
                      : same.receiverBuildingHouseNumber?.slice(-2);

                  const infoFrontDoorPassword = infoFrontDoorPasswords.find(
                    (e) =>
                      ((!e.buildingDongNumber && !buildingDongNumber) ||
                        Number(e.buildingDongNumber) ===
                          Number(buildingDongNumber)) &&
                      ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                        Number(e.buildingHouseNumber) ===
                          Number(buildingHouseNumber)),
                  );

                  if (infoFrontDoorPassword) {
                    infoWindowContent += `<div class="sub-info-box sub-info-password">${infoFrontDoorPassword.password}</div>`;
                  }
                }

                if (infoCses.length > 0) {
                  const buildingDongNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingDongNumber
                      : same.receiverBuildingDongNumber;
                  const buildingHouseNumber =
                    markerDelivery.status === STATUS_PICKUP
                      ? same.senderBuildingHouseNumber
                      : same.receiverBuildingHouseNumber;

                  const infoCs = infoCses.find(
                    (e) =>
                      ((!e.buildingDongNumber && !buildingDongNumber) ||
                        Number(e.buildingDongNumber) ===
                          Number(buildingDongNumber)) &&
                      ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                        Number(e.buildingHouseNumber) ===
                          Number(buildingHouseNumber)),
                  );

                  if (infoCs) {
                    infoWindowContent += `<div class="sub-info-box sub-info-black">주의: ${renderCsCategory(
                      infoCs._category,
                    )}</div>`;
                  }
                }

                if (infoReviews.length > 0) {
                  const buildingDongNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingDongNumber
                      : same.receiverBuildingDongNumber;
                  const buildingHouseNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingHouseNumber
                      : same.receiverBuildingHouseNumber;

                  const infoReview = infoReviews.find(
                    (e) =>
                      ((!e.buildingDongNumber && !buildingDongNumber) ||
                        Number(e.buildingDongNumber) ===
                          Number(buildingDongNumber)) &&
                      ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                        Number(e.buildingHouseNumber) ===
                          Number(buildingHouseNumber)),
                  );

                  if (infoReview) {
                    infoWindowContent += `<div class="sub-info-box sub-info-black">주의: ${renderReviewCategory(
                      infoReview._category,
                    )}</div>`;
                  }
                }

                infoWindowContent += `
                  </div>
                    ${
                      same.status === STATUS_PICKUP ||
                      same.statusAddressNotSupported === STATUS_PICKUP
                        ? ""
                        : `
                      <div class="info-icons">
                        <div class="info-icon highPrice ${
                          !same.isHighPrice && "disabled"
                        }">
                          <img style="width: 6px;" src="${mapImages.iconDollar}" />
                        </div>
                        <div class="info-icon time ${
                          !same.isLimitedHours && "disabled"
                        }">
                          <img style="width: 6px;" src="${mapImages.iconClock}" />
                        </div>
                        <div class="info-icon rearrange ${
                          !same.reallocated && "disabled"
                        }">
                          <img style="width: 8px;" src="${mapImages.iconRotation}" />
                        </div>
                        <div class="info-icon deliveryWait ${
                          sameReceiverWaitDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          ${sameReceiverWaitDeliveriesArr.length}
                        </div>
                        <div class="info-icon deliveryStart ${
                          sameReceiverStartedDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          ${sameReceiverStartedDeliveriesArr.length}
                        </div>
                        <div class="info-icon deliveryPostponed ${
                          sameReceiverPostponedDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          <img style="width: 7px;" src="${mapImages.iconPostponed}" />
                        </div>
                        <div class="info-icon translated ${
                          !same.isTranslated && "disabled"
                        }">
                          <img style="width: 7px;" src="${mapImages.iconTranslated}" />
                        </div>
                      </div>
                    `
                    }
                    <div class="info-title ${same.statusText}">
                      <span style="color: ${same.keyColorHex}">(${
                        same.statusString
                      })</span><span style="${orderingNumberColor === "red" ? "color: red" : ""}">${sameOrderingNumberByDeliveryRiderId}</span>&nbsp;
                      ${same.bookId}&nbsp;
                      <span style="color: ${same.keyColorHex}">${
                        same.typeString
                      }</span>
                    </div>
                    <div class="info-spot">
                      ${
                        same.status === STATUS_PICKUP ||
                        same.statusAddressNotSupported === STATUS_PICKUP
                          ? `${same.spotName} ${
                              same.spot ? `(${same.renderSenderName})` : ""
                            }`
                          : `${same.renderReceiverName}(${same.renderSenderName})`
                      }
                      <span class="c-red">
                        ${
                          same.status !== STATUS_PICKUP &&
                          same.statusAddressNotSupported !== STATUS_PICKUP &&
                          same.isTranslatedWithNoReceiverPoint
                            ? " (번역주의)"
                            : ""
                        }
                        ${
                          same.status !== STATUS_PICKUP &&
                          same.statusAddressNotSupported !== STATUS_PICKUP &&
                          !same.isTranslatedWithNoReceiverPoint &&
                          same.isTranslated
                            ? " (영어주소)"
                            : ""
                        }
                      </span>
                    </div>
                    <div class="info-base-address">
                      ${
                        same.type === DELIVERY_TYPE_NORMAL
                          ? `${
                              same.displayReceiverBuildingDongHouseNumber ||
                              "없음"
                            }(${same.renderAddressApiType})`
                          : same.type !== DELIVERY_TYPE_NORMAL
                            ? `${
                                same.displaySenderBuildingDongHouseNumber ||
                                "없음"
                              }(${same.renderAddressApiType})`
                            : ""
                      } 
                      ${
                        same.frontdoorPassword
                          ? ` / ${same.frontdoorPassword}`
                          : ""
                      }        
                    </div>
                    ${
                      same.renderRequestedDeliveryTime ||
                      same.renderLimitedDeliveryAt
                        ? `<div class="info-reqeusted-time">${
                            same.renderRequestedDeliveryTime ||
                            same.renderLimitedDeliveryAt ||
                            ""
                          }</div>`
                        : ""
                    }
                    <div class="info-address">
                      ${
                        same.status === STATUS_PICKUP ||
                        same.statusAddressNotSupported === STATUS_PICKUP
                          ? `${
                              same.deliveryPlace &&
                              (same.type === DELIVERY_TYPE_PICKUP ||
                                same.type === DELIVERY_TYPE_RETURN)
                                ? `<span class="bg-red">보관등록</span> `
                                : ""
                            }(출)${
                              same.renderRefinedAddressRawResultError
                                ? `<span class="c-red">(${same.renderRefinedAddressRawResultError})</span>`
                                : ""
                            } ${
                              same.senderAddressRoad || same.senderAddress || ""
                            }, ${same.senderAddressDetail}`
                          : `${
                              same.soojioneLimitedCustomized
                                ? `<span class="bg-red">주의${same.distanceRefineToLimitedRefined}</span> `
                                : ""
                            }${
                              same.spotKurlyDrink || same.mustSignature
                                ? `<span class="bg-red">서명</span> `
                                : ""
                            }(도)${
                              same.renderRefinedAddressRawResultError
                                ? `<span class="c-red">(${same.renderRefinedAddressRawResultError})</span>`
                                : ""
                            } ${
                              same.status === STATUS_PICKUP ||
                              same.statusAddressNotSupported === STATUS_PICKUP
                                ? `${
                                    same.senderAddressRoadWithoutSigungu ||
                                    same.senderAddressWithoutSigungu
                                  } ${
                                    same.senderAddressObj &&
                                    same.senderAddressObj.building
                                      ? ` ${same.senderAddressObj.building}`
                                      : ""
                                  }`
                                : `${
                                    same.receiverAddressRoadWithoutSigungu ||
                                    same.receiverAddressWithoutSigungu
                                  }${
                                    same.receiverAddressObj &&
                                    same.receiverAddressObj.building
                                      ? ` ${same.receiverAddressObj.building}`
                                      : ""
                                  }${
                                    same.receiverAddressRoadWithoutSigungu &&
                                    same.senderAddressWithoutSigungu
                                      ? `/${same.senderAddressWithoutSigungu}`
                                      : ""
                                  }${
                                    !same.receiverAddressRoadWithoutSigungu &&
                                    same.receiverDong
                                      ? `/${same.receiverDong}`
                                      : ""
                                  }`
                            } ${
                              same.memoFromCustomer
                                ? `<span class="c-red">(${same.memoFromCustomer})</span>`
                                : ""
                            }`
                      }
                    </div>
                    <div class="info-address bold">
                      ${
                        same.status !== STATUS_PICKUP &&
                        same.statusAddressNotSupported !== STATUS_PICKUP &&
                        same.customerAddressEngGpt
                          ? `(GPT번역) ${same.customerAddressEngGpt}`
                          : ""
                      }
                    </div>
                    <div class="info-address">
                      ${same.displayAddress}
                    </div>
                    <div class="info-address bold">
                      ${
                        same.status !== STATUS_PICKUP &&
                        same.statusAddressNotSupported !== STATUS_PICKUP &&
                        same.isTranslatedWithNoReceiverPoint
                          ? `(번역주의) ${same.originAddressEng}`
                          : ""
                      }
                    </div>
                  </div>
                `;

                if (
                  (same.status === STATUS_PICKUP ||
                    same.statusAddressNotSupported === STATUS_PICKUP) &&
                  same.type === DELIVERY_TYPE_NORMAL
                ) {
                  pickupInfoWindowDone = true;
                }
              }
            });
          }

          // 툴팁 : 동일 주소 목록(수거건) - 배송 출발 건/CS만 표시됨
          if (
            markerDelivery.samePlacesPickup &&
            markerDelivery.samePlacesPickup.length > 0
          ) {
            markerDelivery.samePlacesPickup.forEach((same) => {
              if (
                same.status === STATUS_DELIVERY_WAITING ||
                same.status === STATUS_DELIVERY_POSTPONED ||
                same.status === STATUS_DELIVERY_STARTED ||
                same.statusAddressNotSupported === STATUS_DELIVERY_STARTED ||
                same.type === DELIVERY_TYPE_PICKUP ||
                same.type === DELIVERY_TYPE_RETURN ||
                !pickupInfoWindowDone
              ) {
                let linkingDetail = `window.__markMapCameraRestore&&window.__markMapCameraRestore();window.ReactNativeWebView.postMessage('${JSON.stringify(
                  {
                    type: "detail",
                    bookId: same.bookId,
                  },
                )}');`;

                if (!appVersion) {
                  linkingDetail = `window.__markMapCameraRestore&&window.__markMapCameraRestore();window.ReactNativeWebView.postMessage('${same.bookId}');`;
                }

                let sameReceiverWaitDeliveriesArr =
                  same.sameReceiverDeliveriesObj?.waiting || [];
                let sameReceiverStartedDeliveriesArr =
                  same.sameReceiverDeliveriesObj?.started || [];
                let sameReceiverPostponedDeliveriesArr =
                  same.sameReceiverDeliveriesObj?.postponed || [];

                let sameOrderingNumberByDeliveryRiderId = "";
                // let sameIsUploadedOrderingNumber = true;

                if (same.orderingNumberByDeliveryRiderId) {
                  sameOrderingNumberByDeliveryRiderId = ` [${same.orderingNumberByDeliveryRiderId}]`;

                  // if (!same.isUploadedOrderingNumber) {
                  //   sameIsUploadedOrderingNumber = false;
                  // }
                }

                infoWindowContent += `<div 
                    class="info ${same.statusText} ${same.keyColor}" 
                    style="padding: 5px 10px 10px; max-width: 250px; position: relative;" 
                    onclick=${linkingDetail}
                  >
                  <div class="sub-info">`;

                if (infoFrontDoorPasswords.length > 0) {
                  const buildingDongNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingDongNumber
                      : same.receiverBuildingDongNumber;
                  const buildingHouseNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingHouseNumber?.slice(-2)
                      : same.receiverBuildingHouseNumber?.slice(-2);

                  const infoFrontDoorPassword = infoFrontDoorPasswords.find(
                    (e) =>
                      ((!e.buildingDongNumber && !buildingDongNumber) ||
                        Number(e.buildingDongNumber) ===
                          Number(buildingDongNumber)) &&
                      ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                        Number(e.buildingHouseNumber) ===
                          Number(buildingHouseNumber)),
                  );

                  if (infoFrontDoorPassword) {
                    infoWindowContent += `<div class="sub-info-box sub-info-password">${infoFrontDoorPassword.password}</div>`;
                  }
                }

                if (infoCses.length > 0) {
                  const buildingDongNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingDongNumber
                      : same.receiverBuildingDongNumber;
                  const buildingHouseNumber =
                    markerDelivery.status === STATUS_PICKUP
                      ? same.senderBuildingHouseNumber
                      : same.receiverBuildingHouseNumber;

                  const infoCs = infoCses.find(
                    (e) =>
                      ((!e.buildingDongNumber && !buildingDongNumber) ||
                        Number(e.buildingDongNumber) ===
                          Number(buildingDongNumber)) &&
                      ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                        Number(e.buildingHouseNumber) ===
                          Number(buildingHouseNumber)),
                  );

                  if (infoCs) {
                    infoWindowContent += `<div class="sub-info-box sub-info-black">주의: ${renderCsCategory(
                      infoCs._category,
                    )}</div>`;
                  }
                }

                if (infoReviews.length > 0) {
                  const buildingDongNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingDongNumber
                      : same.receiverBuildingDongNumber;
                  const buildingHouseNumber =
                    same.status === STATUS_PICKUP
                      ? same.senderBuildingHouseNumber
                      : same.receiverBuildingHouseNumber;

                  const infoReview = infoReviews.find(
                    (e) =>
                      ((!e.buildingDongNumber && !buildingDongNumber) ||
                        Number(e.buildingDongNumber) ===
                          Number(buildingDongNumber)) &&
                      ((!e.buildingHouseNumber && !buildingHouseNumber) ||
                        Number(e.buildingHouseNumber) ===
                          Number(buildingHouseNumber)),
                  );

                  if (infoReview) {
                    infoWindowContent += `<div class="sub-info-box sub-info-black">주의: ${renderReviewCategory(
                      infoReview._category,
                    )}</div>`;
                  }
                }

                infoWindowContent += `
                  </div>
                    ${
                      same.status === STATUS_PICKUP ||
                      same.statusAddressNotSupported === STATUS_PICKUP
                        ? ""
                        : `
                      <div class="info-icons">
                        <div class="info-icon highPrice ${
                          !same.isHighPrice && "disabled"
                        }">
                          <img style="width: 6px;" src="${mapImages.iconDollar}" />
                        </div>
                        <div class="info-icon time ${
                          !same.isLimitedHours && "disabled"
                        }">
                          <img style="width: 6px;" src="${mapImages.iconClock}" />
                        </div>
                        <div class="info-icon rearrange ${
                          !same.reallocated && "disabled"
                        }">
                          <img style="width: 8px;" src="${mapImages.iconRotation}" />
                        </div>
                        <div class="info-icon deliveryWait ${
                          sameReceiverWaitDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          ${sameReceiverWaitDeliveriesArr.length}
                        </div>
                        <div class="info-icon deliveryStart ${
                          sameReceiverStartedDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          ${sameReceiverStartedDeliveriesArr.length}
                        </div>
                        <div class="info-icon deliveryPostponed ${
                          sameReceiverPostponedDeliveriesArr.length === 0 &&
                          "disabled"
                        }">
                          <img style="width: 7px;" src="${mapImages.iconPostponed}" />
                        </div>
                        <div class="info-icon translated ${
                          !same.isTranslated && "disabled"
                        }">
                          <img style="width: 7px;" src="${mapImages.iconTranslated}" />
                        </div>
                      </div>
                    `
                    }
                    <div class="info-title ${same.statusText}">
                      <span style="color: ${same.keyColorHex}">(${
                        same.statusString
                      })</span><span style="${orderingNumberColor === "red" ? "color: red" : ""}">${sameOrderingNumberByDeliveryRiderId}</span>&nbsp;
                      ${same.bookId}&nbsp;
                      <span style="color: ${same.keyColorHex}">${
                        same.typeString
                      }</span>
                    </div>
                    <div class="info-spot">
                      ${
                        same.status === STATUS_PICKUP ||
                        same.statusAddressNotSupported === STATUS_PICKUP
                          ? `${same.spotName} ${
                              same.spot ? `(${same.renderSenderName})` : ""
                            }`
                          : `${same.renderReceiverName}(${same.renderSenderName})`
                      }
                      <span class="c-red">
                        ${
                          same.status !== STATUS_PICKUP &&
                          same.statusAddressNotSupported !== STATUS_PICKUP &&
                          same.isTranslatedWithNoReceiverPoint
                            ? " (번역주의)"
                            : ""
                        }
                        ${
                          same.status !== STATUS_PICKUP &&
                          same.statusAddressNotSupported !== STATUS_PICKUP &&
                          !same.isTranslatedWithNoReceiverPoint &&
                          same.isTranslated
                            ? " (영어주소)"
                            : ""
                        }
                      </span>
                    </div>
                    <div class="info-base-address">
                      ${
                        same.type === DELIVERY_TYPE_NORMAL
                          ? `${
                              same.displayReceiverBuildingDongHouseNumber ||
                              "없음"
                            }(${same.renderAddressApiType})`
                          : same.type !== DELIVERY_TYPE_NORMAL
                            ? `${
                                same.displaySenderBuildingDongHouseNumber ||
                                "없음"
                              }(${same.renderAddressApiType})`
                            : ""
                      } 
                      ${
                        same.frontdoorPassword
                          ? ` / ${same.frontdoorPassword}`
                          : ""
                      }        
                    </div>
                       ${
                         same.renderRequestedDeliveryTime ||
                         same.renderLimitedDeliveryAt
                           ? `<div class="info-reqeusted-time">${
                               same.renderRequestedDeliveryTime ||
                               same.renderLimitedDeliveryAt ||
                               ""
                             }</div>`
                           : ""
                       }
                    <div class="info-address">
                      ${
                        same.status === STATUS_PICKUP ||
                        same.statusAddressNotSupported === STATUS_PICKUP
                          ? `${
                              same.deliveryPlace &&
                              (same.type === DELIVERY_TYPE_PICKUP ||
                                same.type === DELIVERY_TYPE_RETURN)
                                ? `<span class="bg-red">보관등록</span> `
                                : ""
                            }(출)${
                              same.renderRefinedAddressRawResultError
                                ? `<span class="c-red">(${same.renderRefinedAddressRawResultError})</span>`
                                : ""
                            } ${
                              same.senderAddressRoad || same.senderAddress || ""
                            }, ${same.senderAddressDetail}`
                          : `${
                              same.soojioneLimitedCustomized
                                ? `<span class="bg-red">주의${same.distanceRefineToLimitedRefined}</span> `
                                : ""
                            }${
                              same.spotKurlyDrink || same.mustSignature
                                ? `<span class="bg-red">서명</span> `
                                : ""
                            }(도)${
                              same.renderRefinedAddressRawResultError
                                ? `<span class="c-red">(${same.renderRefinedAddressRawResultError})</span>`
                                : ""
                            } ${
                              same.status === STATUS_PICKUP ||
                              same.statusAddressNotSupported === STATUS_PICKUP
                                ? `${
                                    same.senderAddressRoadWithoutSigungu ||
                                    same.senderAddressWithoutSigungu
                                  } ${
                                    same.senderAddressObj &&
                                    same.senderAddressObj.building
                                      ? ` ${same.senderAddressObj.building}`
                                      : ""
                                  }`
                                : `${
                                    same.receiverAddressRoadWithoutSigungu ||
                                    same.receiverAddressWithoutSigungu
                                  }${
                                    same.receiverAddressObj &&
                                    same.receiverAddressObj.building
                                      ? ` ${same.receiverAddressObj.building}`
                                      : ""
                                  }${
                                    same.receiverAddressRoadWithoutSigungu &&
                                    same.senderAddressWithoutSigungu
                                      ? `/${same.senderAddressWithoutSigungu}`
                                      : ""
                                  }${
                                    !same.receiverAddressRoadWithoutSigungu &&
                                    same.receiverDong
                                      ? `/${same.receiverDong}`
                                      : ""
                                  }`
                            } ${
                              same.memoFromCustomer
                                ? `<span class="c-red">(${same.memoFromCustomer})</span>`
                                : ""
                            }`
                      }
                    </div>
                    <div class="info-address bold">
                      ${
                        same.status !== STATUS_PICKUP &&
                        same.statusAddressNotSupported !== STATUS_PICKUP &&
                        same.customerAddressEngGpt
                          ? `(GPT번역) ${same.customerAddressEngGpt}`
                          : ""
                      }
                    </div>
                    <div class="info-address">
                      ${same.displayAddress}
                    </div>
                    <div class="info-address bold">
                      ${
                        same.status !== STATUS_PICKUP &&
                        same.statusAddressNotSupported !== STATUS_PICKUP &&
                        same.isTranslatedWithNoReceiverPoint
                          ? `(번역주의) ${same.originAddressEng}`
                          : ""
                      }
                    </div>
                  </div>
                `;

                if (
                  (same.status === STATUS_PICKUP ||
                    same.statusAddressNotSupported === STATUS_PICKUP) &&
                  same.type === DELIVERY_TYPE_NORMAL
                ) {
                  pickupInfoWindowDone = true;
                }
              }
            });
          }

          // infoWindowContent += `<div class="sub-info">${infoFrontDoorPassword}${infoCses.join(
          //   "",
          // )}${infoReviews.join("")}</div>`;

          infoWindowContent += "</div></div>";

          infoWindow.setContent(infoWindowContent);

          infoWindow.open(map, marker);

          if (
            marker.soojioneLimitedCustomized &&
            marker.soojioneLimitedCustomizedLngLat &&
            marker.status !== STATUS_PICKUP &&
            marker.statusAddressNotSupported !== STATUS_PICKUP &&
            (marker.soojioneLimitedCustomizedLngLat?.latitude !==
              marker.latitude ||
              marker.soojioneLimitedCustomizedLngLat?.longitude !==
                marker.longitude)
          ) {
            // soojione 마커
            let markerSoojiwonIcon = {
              size: new window.naver.maps.Size(23, 32),
              scaledSize: new window.naver.maps.Size(23, 32),
              content: `
                  <div class="marker soojione">
                    ${
                      showMarkerAddress === "ALL" &&
                      marker.soojioneLimitedCustomizedAddressRoadWithoutSigungu
                        ? `<div class="marker-address">
                        ${marker.soojioneLimitedCustomizedAddressRoadWithoutSigungu}</div>`
                        : ""
                    }
                  </div>
                `,
            };

            let markerSoojiwon = new window.naver.maps.Marker({
              map: map,
              position: new window.naver.maps.LatLng(
                marker.soojioneLimitedCustomizedLngLat?.latitude,
                marker.soojioneLimitedCustomizedLngLat?.longitude,
              ),
              title: "SOOJIWON",
              icon: markerSoojiwonIcon,
              zIndex: 100,
            });

            markersSoojione.push(markerSoojiwon);

            let polylinePath = [
              new window.naver.maps.LatLng(marker.latitude, marker.longitude),
              new window.naver.maps.LatLng(
                marker.soojioneLimitedCustomizedLngLat?.latitude,
                marker.soojioneLimitedCustomizedLngLat?.longitude,
              ),
            ];

            //위의 배열을 이용해 라인 그리기
            const polyline = new window.naver.maps.Polyline({
              path: polylinePath, //선 위치 변수배열
              strokeColor: "#FF0000", //선 색 빨강 #빨강,초록,파랑
              strokeOpacity: 0.8, //선 투명도 0 ~ 1
              strokeWeight: 4, //선 두께
              strokeStyle: "shortdash",
              map: map, //오버레이할 지도
            });

            polylines.push(polyline);
          }

          // if (baseAddressEntryPoints?.length > 0) {
          //   const baseAddressEntryPoint = baseAddressEntryPoints[0];

          //   if (
          //     baseAddressEntryPoint.entryPointLat !== marker.latitude ||
          //     baseAddressEntryPoint.entryPointLng !== marker.longitude
          //   ) {
          //     // baseAddressEntryPoint 마커
          //     let markerBaseAddressEntryPointIcon = {
          //       size: new window.naver.maps.Size(24, 24),
          //       scaledSize: new window.naver.maps.Size(24, 24),
          //       content: `
          //         <div class="marker-frontdoor"></div>
          //       `,
          //     };

          //     let markerBaseAddressEntryPoint = new window.naver.maps.Marker({
          //       map: map,
          //       position: new window.naver.maps.LatLng(
          //         baseAddressEntryPoint.entryPointLat,
          //         baseAddressEntryPoint.entryPointLng,
          //       ),
          //       title: "BASE_ADDRESS_ENTRY_POINT",
          //       icon: markerBaseAddressEntryPointIcon,
          //       zIndex: 100,
          //     });

          //     markersBaseAddressEntryPoint.push(markerBaseAddressEntryPoint);

          //     let polylinePath = [
          //       new window.naver.maps.LatLng(marker.latitude, marker.longitude),
          //       new window.naver.maps.LatLng(
          //         baseAddressEntryPoint.entryPointLat,
          //         baseAddressEntryPoint.entryPointLng,
          //       ),
          //     ];

          //     //위의 배열을 이용해 라인 그리기
          //     const polyline = new window.naver.maps.Polyline({
          //       path: polylinePath, //선 위치 변수배열
          //       strokeColor: "#323232", //선 색 빨강 #빨강,초록,파랑
          //       strokeOpacity: 0.8, //선 투명도 0 ~ 1
          //       strokeWeight: 2, //선 두께
          //       strokeStyle: "shortdash",
          //       map: map, //오버레이할 지도
          //     });

          //     polylinesBaseAddressEntryPoint.push(polyline);
          //   }
          // }
        }
      }
    };
  };

  // 지도 영역 밖 마커
  const [hideMarkers, setHideMarkers] = useState([]);

  const updateMarkers = (map, markers) => {
    let mapBounds = map.getBounds();
    let marker, position;

    let _hiderMarkers = [];

    for (var i = 0; i < markers.length; i++) {
      marker = markers[i];
      position = marker.getPosition();

      if (mapBounds.hasLatLng(position)) {
        showMarker(map, marker);
      } else {
        hideMarker(marker);

        if (marker.title !== "SOOJIWON") {
          _hiderMarkers.push(marker);
        }
      }
    }

    setHideMarkers(_hiderMarkers);
  };

  // 두 좌표 간 거리 계산 (미터, Haversine)
  const calcDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const drawMarker = () => {
    for (let i = 0; i < markers.length; i++) {
      markers[i].setMap(null);

      var infoWindow = infoWindows[i];

      if (infoWindow.getMap()) {
        infoWindow.close();
      }
    }

    for (let i = 0; i < polylines.length; i++) {
      polylines[i].setMap(null);
    }

    for (let i = 0; i < polylinesBaseAddressEntryPoint.length; i++) {
      polylinesBaseAddressEntryPoint[i].setMap(null);
    }

    for (let i = 0; i < markersSoojione.length; i++) {
      markersSoojione[i].setMap(null);
    }

    for (let i = 0; i < markersBaseAddressEntryPoint.length; i++) {
      markersBaseAddressEntryPoint[i].setMap(null);
    }

    markers = [];
    infoWindows = [];

    if (markerList.length > 0) {
      markerList.forEach((item) => {
        // 순서지정 모드에서, 위치추적(강제 순서지정) 대상건이 하나라도 있는 회차라면
        // 대상건이 아닌 마커는 표시하지 않는다.
        // 위치추적 대상건이 아예 없는 회차(위치추적 미사용 기업회원)라면 전체 배송출발건을 그대로 표시한다.
        if (
          isOrderMode &&
          deliveriesStartedWithLocationTracking.length > 0 &&
          !item.corpUserEnableRiderLocationTracking
        ) {
          return;
        }

        // 마커 옆 아이콘
        let isHighPrice = false;
        let isLimitedHours = false;
        let isReallocated = false;
        let isTranslated = false;
        let receiverWaitDeliveriesCount = 0;
        let receiverStartedDeliveriesCount = 0;
        let receiverPostponedDeliveriesCount = 0;
        let isNearbyMarker = false;
        let isNearbyMarkerInOtherStatus = false;

        let isDeliveryPlace = false;

        const effectiveOrder = Math.min(
          deliveryRoutesMap[bookIdKey(item.bookId)]?.sequence ?? item.order,
          ...(item.samePlaces?.map(
            (place) =>
              deliveryRoutesMap[bookIdKey(place.bookId)]?.sequence ??
              place.order,
          ) ?? []),
        );

        const memoForCjoyquickapi = item.memoForCjoyquickapi
          ? item.memoForCjoyquickapi
          : (item.samePlaces?.find((place) => place.memoForCjoyquickapi)
              ?.memoForCjoyquickapi ?? "");
        const memoForKurly = item.memoForKurly
          ? item.memoForKurly
          : (item.samePlaces?.find((place) => place.memoForKurly)
              ?.memoForKurly ?? "");

        // 같은 위치에 마커가 있을 경우 마커를 표시하지 않는다.
        // const prevMarkerIndex = markers.findIndex(
        //   (d) =>
        //     d.title !== item.bookId &&
        //     d.position.y === Number(item.latitude) &&
        //     d.position.x === Number(item.longitude),
        // );

        // if (prevMarkerIndex > -1) {
        //   markers.splice(prevMarkerIndex, 1);
        //   infoWindows.splice(prevMarkerIndex, 1);
        // }

        if (item.isHighPrice) {
          isHighPrice = true;
        }
        if (item.isLimitedHours) {
          isLimitedHours = true;
        }
        if (item.reallocated) {
          isReallocated = true;
        }
        if (item.isTranslated) {
          isTranslated = true;
        }
        if (
          (item.status === STATUS_PICKUP ||
            item.statusAddressNotSupported === STATUS_PICKUP) &&
          (item.type === DELIVERY_TYPE_PICKUP ||
            item.type === DELIVERY_TYPE_RETURN) &&
          item.deliveryPlace
        ) {
          isDeliveryPlace = true;
        }

        // 수거일경우 동일 주소 툴팁 표시 제외
        let pickupInfoWindowDone = false;

        // 동일수 하인 동일 주소의 개수
        let receiverWaitDeliveriesArr =
          item.sameReceiverDeliveriesObj.waiting || [];
        let receiverStartedDeliveriesArr =
          item.sameReceiverDeliveriesObj.started || [];
        let receiverPostponedDeliveriesArr =
          item.sameReceiverDeliveriesObj.postponed || [];

        if (
          (item.status === STATUS_DELIVERY_WAITING ||
            item.status === STATUS_DELIVERY_POSTPONED ||
            item.status === STATUS_DELIVERY_STARTED ||
            item.statusAddressNotSupported === STATUS_DELIVERY_STARTED) &&
          deliveriesAllocated &&
          deliveriesAllocated.length > 0
        ) {
          receiverWaitDeliveriesCount = receiverWaitDeliveriesArr.length;
          receiverStartedDeliveriesCount = receiverStartedDeliveriesArr.length;
          receiverPostponedDeliveriesCount =
            receiverPostponedDeliveriesArr.length;
        }

        // 근접 마커 여부 표시
        const { nearbyMarkersArr, nearbyMarkersInOtherStatusArr } =
          checkNearbyMarkers(item, markerListWithSameplace);
        if (nearbyMarkersArr.length > 0) {
          isNearbyMarker = true;
        } else {
          isNearbyMarker = false;
        }

        // 근접 마커 여부 표시(다른 상태값)
        if (nearbyMarkersInOtherStatusArr.length > 0) {
          isNearbyMarkerInOtherStatus = true;
        } else {
          isNearbyMarkerInOtherStatus = false;
        }

        // 툴팁 내용
        let infoWindowContent = ``;

        let orderingNumbersByDeliveryRiderId = [];
        // let isUploadedOrderingNumbers = true;

        if (item.orderingNumberByDeliveryRiderId) {
          orderingNumbersByDeliveryRiderId.push(
            item.orderingNumberByDeliveryRiderId,
          );

          // if (!item.isUploadedOrderingNumber) {
          //   isUploadedOrderingNumbers = false;
          // }
        }

        if (
          (item.status === STATUS_PICKUP ||
            item.statusAddressNotSupported === STATUS_PICKUP) &&
          item.type === DELIVERY_TYPE_NORMAL
        ) {
          pickupInfoWindowDone = true;
        }

        // 툴팁 : 동일 주소 목록
        if (item.samePlaces && item.samePlaces.length > 0) {
          item.samePlaces.forEach((same) => {
            if (
              same.status === STATUS_DELIVERY_WAITING ||
              same.status === STATUS_DELIVERY_POSTPONED ||
              same.status === STATUS_DELIVERY_STARTED ||
              same.statusAddressNotSupported === STATUS_DELIVERY_STARTED ||
              same.type === DELIVERY_TYPE_PICKUP ||
              same.type === DELIVERY_TYPE_RETURN ||
              !pickupInfoWindowDone
            ) {
              if (same.isHighPrice) {
                isHighPrice = true;
              }
              if (same.isLimitedHours) {
                isLimitedHours = true;
              }
              if (same.reallocated) {
                isReallocated = true;
              }
              if (same.isTranslated) {
                isTranslated = true;
              }
              if (
                (same.status === STATUS_PICKUP ||
                  same.statusAddressNotSupported === STATUS_PICKUP) &&
                (same.type === DELIVERY_TYPE_PICKUP ||
                  same.type === DELIVERY_TYPE_RETURN) &&
                same.deliveryPlace
              ) {
                isDeliveryPlace = true;
              }

              let sameReceiverWaitDeliveriesArr =
                same.sameReceiverDeliveriesObj?.waiting || [];
              let sameReceiverStartedDeliveriesArr =
                same.sameReceiverDeliveriesObj?.started || [];
              let sameReceiverPostponedDeliveriesArr =
                same.sameReceiverDeliveriesObj?.postponed || [];

              if (
                (same.status === STATUS_DELIVERY_WAITING ||
                  same.status === STATUS_DELIVERY_POSTPONED ||
                  same.status === STATUS_DELIVERY_STARTED ||
                  same.statusAddressNotSupported === STATUS_DELIVERY_STARTED) &&
                deliveriesAllocated &&
                deliveriesAllocated.length > 0
              ) {
                if (
                  sameReceiverWaitDeliveriesArr.length >
                  receiverWaitDeliveriesCount
                ) {
                  receiverWaitDeliveriesCount =
                    sameReceiverWaitDeliveriesArr.length;
                }
                if (
                  sameReceiverStartedDeliveriesArr.length >
                  receiverStartedDeliveriesCount
                ) {
                  receiverStartedDeliveriesCount =
                    sameReceiverStartedDeliveriesArr.length;
                }
                if (
                  sameReceiverPostponedDeliveriesArr.length >
                  receiverPostponedDeliveriesCount
                ) {
                  receiverPostponedDeliveriesCount =
                    sameReceiverPostponedDeliveriesArr.length;
                }
              }

              if (same.orderingNumberByDeliveryRiderId) {
                orderingNumbersByDeliveryRiderId.push(
                  same.orderingNumberByDeliveryRiderId,
                );

                // if (!same.isUploadedOrderingNumber) {
                //   isUploadedOrderingNumbers = false;
                // }
              }

              if (
                (same.status === STATUS_PICKUP ||
                  same.statusAddressNotSupported === STATUS_PICKUP) &&
                same.type === DELIVERY_TYPE_NORMAL
              ) {
                pickupInfoWindowDone = true;
              }
            }
          });
        }

        // 툴팁 : 동일 주소 목록(배송건) - CS만 표시됨
        if (item.samePlacesDelivery && item.samePlacesDelivery.length > 0) {
          item.samePlacesDelivery.forEach((same) => {
            if (
              same.status === STATUS_DELIVERY_WAITING ||
              same.status === STATUS_DELIVERY_POSTPONED ||
              same.status === STATUS_DELIVERY_STARTED ||
              same.statusAddressNotSupported === STATUS_DELIVERY_STARTED ||
              same.type === DELIVERY_TYPE_PICKUP ||
              same.type === DELIVERY_TYPE_RETURN ||
              !pickupInfoWindowDone
            ) {
              if (same.isHighPrice) {
                isHighPrice = true;
              }
              if (same.isLimitedHours) {
                isLimitedHours = true;
              }
              if (same.reallocated) {
                isReallocated = true;
              }
              if (same.isTranslated) {
                isTranslated = true;
              }
              if (
                (same.status === STATUS_PICKUP ||
                  same.statusAddressNotSupported === STATUS_PICKUP) &&
                (same.type === DELIVERY_TYPE_PICKUP ||
                  same.type === DELIVERY_TYPE_RETURN) &&
                same.deliveryPlace
              ) {
                isDeliveryPlace = true;
              }

              let sameReceiverWaitDeliveriesArr =
                same.sameReceiverDeliveriesObj?.waiting || [];
              let sameReceiverStartedDeliveriesArr =
                same.sameReceiverDeliveriesObj?.started || [];
              let sameReceiverPostponedDeliveriesArr =
                same.sameReceiverDeliveriesObj?.postponed || [];

              if (
                (same.status === STATUS_DELIVERY_WAITING ||
                  same.status === STATUS_DELIVERY_POSTPONED ||
                  same.status === STATUS_DELIVERY_STARTED ||
                  same.statusAddressNotSupported === STATUS_DELIVERY_STARTED) &&
                deliveriesAllocated &&
                deliveriesAllocated.length > 0
              ) {
                if (
                  sameReceiverWaitDeliveriesArr.length >
                  receiverWaitDeliveriesCount
                ) {
                  receiverWaitDeliveriesCount =
                    sameReceiverWaitDeliveriesArr.length;
                }
                if (
                  sameReceiverStartedDeliveriesArr.length >
                  receiverStartedDeliveriesCount
                ) {
                  receiverStartedDeliveriesCount =
                    sameReceiverStartedDeliveriesArr.length;
                }
                if (
                  sameReceiverPostponedDeliveriesArr.length >
                  receiverPostponedDeliveriesCount
                ) {
                  receiverPostponedDeliveriesCount =
                    sameReceiverPostponedDeliveriesArr.length;
                }
              }

              if (same.orderingNumberByDeliveryRiderId) {
                orderingNumbersByDeliveryRiderId.push(
                  same.orderingNumberByDeliveryRiderId,
                );

                // if (!same.isUploadedOrderingNumber) {
                //   isUploadedOrderingNumbers = false;
                // }
              }

              if (
                (same.status === STATUS_PICKUP ||
                  same.statusAddressNotSupported === STATUS_PICKUP) &&
                same.type === DELIVERY_TYPE_NORMAL
              ) {
                pickupInfoWindowDone = true;
              }
            }
          });
        }

        // 툴팁 : 동일 주소 목록(수거건) - 배송 출발 건/CS만 표시됨
        if (item.samePlacesPickup && item.samePlacesPickup.length > 0) {
          item.samePlacesPickup.forEach((same) => {
            if (
              same.status === STATUS_DELIVERY_WAITING ||
              same.status === STATUS_DELIVERY_POSTPONED ||
              same.status === STATUS_DELIVERY_STARTED ||
              same.statusAddressNotSupported === STATUS_DELIVERY_STARTED ||
              same.type === DELIVERY_TYPE_PICKUP ||
              same.type === DELIVERY_TYPE_RETURN ||
              !pickupInfoWindowDone
            ) {
              if (same.isHighPrice) {
                isHighPrice = true;
              }
              if (same.isLimitedHours) {
                isLimitedHours = true;
              }
              if (same.reallocated) {
                isReallocated = true;
              }
              if (same.isTranslated) {
                isTranslated = true;
              }
              if (
                (same.status === STATUS_PICKUP ||
                  same.statusAddressNotSupported === STATUS_PICKUP) &&
                (same.type === DELIVERY_TYPE_PICKUP ||
                  same.type === DELIVERY_TYPE_RETURN) &&
                same.deliveryPlace
              ) {
                isDeliveryPlace = true;
              }

              let sameReceiverWaitDeliveriesArr =
                same.sameReceiverDeliveriesObj?.waiting || [];
              let sameReceiverStartedDeliveriesArr =
                same.sameReceiverDeliveriesObj?.started || [];
              let sameReceiverPostponedDeliveriesArr =
                same.sameReceiverDeliveriesObj?.postponed || [];

              if (
                (same.status === STATUS_DELIVERY_WAITING ||
                  same.status === STATUS_DELIVERY_POSTPONED ||
                  same.status === STATUS_DELIVERY_STARTED ||
                  same.statusAddressNotSupported === STATUS_DELIVERY_STARTED) &&
                deliveriesAllocated &&
                deliveriesAllocated.length > 0
              ) {
                if (
                  sameReceiverWaitDeliveriesArr.length >
                  receiverWaitDeliveriesCount
                ) {
                  receiverWaitDeliveriesCount =
                    sameReceiverWaitDeliveriesArr.length;
                }
                if (
                  sameReceiverStartedDeliveriesArr.length >
                  receiverStartedDeliveriesCount
                ) {
                  receiverStartedDeliveriesCount =
                    sameReceiverStartedDeliveriesArr.length;
                }
                if (
                  sameReceiverPostponedDeliveriesArr.length >
                  receiverPostponedDeliveriesCount
                ) {
                  receiverPostponedDeliveriesCount =
                    sameReceiverPostponedDeliveriesArr.length;
                }
              }

              if (same.orderingNumberByDeliveryRiderId) {
                orderingNumbersByDeliveryRiderId.push(
                  same.orderingNumberByDeliveryRiderId,
                );

                // if (!same.isUploadedOrderingNumber) {
                //   isUploadedOrderingNumbers = false;
                // }
              }

              if (
                (same.status === STATUS_PICKUP ||
                  same.statusAddressNotSupported === STATUS_PICKUP) &&
                same.type === DELIVERY_TYPE_NORMAL
              ) {
                pickupInfoWindowDone = true;
              }
            }
          });
        }

        orderingNumbersByDeliveryRiderId.sort((a, b) => (a > b ? 1 : -1));

        // 마커 아이콘
        let markerIcon = {
          size: new window.naver.maps.Size(23, 32),
          scaledSize: new window.naver.maps.Size(23, 32),
          content: `
              <div class="marker ${item.statusText} ${
                item.isPenaltyCorpUser ? "Penalty" : ""
              } ${isDeliveryPlace ? "pickupPlace" : ""} ${
                item.samePlacekeyColor || item.keyColor
              }">
                ${
                  isRearrangeMode
                    ? getRearrange(
                        isRearrangeMode,
                        selectedDeliveries,
                        item.bookId,
                      )
                    : orderingNumbersByDeliveryRiderId?.length > 0
                      ? orderingNumbersByDeliveryRiderId[0]
                      : ""
                }
                
                <div class="marker-plus-wrap">
                  ${
                    item.samePlacesCount || item.samePlacesPickupCount
                      ? `<div class="marker-plus">
                        ${
                          item.samePlacesCount > 0
                            ? `+${item.samePlacesCount}`
                            : ""
                        }${
                          item.samePlacesPickupCount > 0
                            ? `(${item.samePlacesPickupCount})`
                            : ""
                        }</div>`
                      : ""
                  }
                  
                  ${
                    isNearbyMarker
                      ? `<div class="marker-near"><img style="width: 14px;" src="${mapImages.iconNear}" /></div>`
                      : ""
                  }
                  ${
                    isNearbyMarkerInOtherStatus
                      ? nearbyMarkersInOtherStatusArr.map(
                          (nearbyMarker, idx) =>
                            `<div class="marker-near delivery ${
                              nearbyMarker.status === STATUS_PICKUP &&
                              "deliveryPickup"
                            } ${
                              nearbyMarker.status === STATUS_DELIVERY_WAITING &&
                              "deliveryWait "
                            } ${
                              nearbyMarker.status === STATUS_DELIVERY_STARTED &&
                              "deliveryStart"
                            } ${
                              nearbyMarker.status ===
                                STATUS_DELIVERY_POSTPONED && "deliveryPostponed"
                            } ${nearbyMarker.status === STATUS_CS && "cs"}"
                        key=${idx}
                      >
                        <img style="width: 7px;" src="${mapImages.iconNearplus}" />
                      </div>`,
                        )
                      : ""
                  }
                </div>
                
                ${
                  item.status === STATUS_PICKUP ||
                  item.statusAddressNotSupported === STATUS_PICKUP
                    ? ``
                    : `
                  <div class="marker-icons ${
                    item.samePlacesCount ||
                    item.samePlacesPickupCount ||
                    isNearbyMarker ||
                    isNearbyMarkerInOtherStatus
                      ? "plus"
                      : ""
                  }">
                    ${
                      isHighPrice
                        ? `<div class="marker-icon highPrice"><img style="width: 6px;" src="${mapImages.iconDollar}" /></div>`
                        : ""
                    }
                    ${
                      isLimitedHours
                        ? `<div class="marker-icon time"><img style="width: 6px;" src="${mapImages.iconClock}" /></div>`
                        : ""
                    }
                    ${
                      isReallocated
                        ? `<div class="marker-icon rearrange"><img style="width: 8px;" src="${mapImages.iconRotation}" /></div>`
                        : ""
                    }
                    ${
                      receiverWaitDeliveriesCount > 0
                        ? `<div class="marker-icon deliveryWait"></div>`
                        : ""
                    }
                    ${
                      receiverStartedDeliveriesCount > 0
                        ? `<div class="marker-icon deliveryStart">${receiverStartedDeliveriesCount}</div>`
                        : ""
                    }
                    ${
                      receiverPostponedDeliveriesCount > 0
                        ? `<div class="marker-icon deliveryPostponed"><img style="width: 7px;" src="${mapImages.iconPostponed}" /></div>`
                        : ""
                    }
                    ${
                      isTranslated
                        ? `<div class="marker-icon translated"><img style="width: 7px;" src="${mapImages.iconTranslated}" /></div>`
                        : ""
                    }
                  </div>
                `
                }
                
                ${
                  item.isTranslated ||
                  // item.order ||
                  showMarkerAddress !== "NONE"
                    ? // orderingNumbersByDeliveryRiderId.length > 0
                      `<div class="marker-address">
                  ${item.isTranslated ? `(영어주소)<br/>` : ""}
                  ${
                    showMarkerAddress !== "NONE" && effectiveOrder
                      ? `${getOrder(
                          false,
                          orderList,
                          newOrderList,
                          effectiveOrder,
                          item.bookId,
                        )}<br/>`
                      : ""
                  }
                  ${
                    showMarkerAddress === "ALL"
                      ? item.status === STATUS_PICKUP ||
                        item.statusAddressNotSupported === STATUS_PICKUP
                        ? `${
                            item.senderAddressRoadWithoutSigungu ||
                            item.senderAddressWithoutSigungu
                          }${
                            item.senderAddressObj &&
                            item.senderAddressObj.building
                              ? `<br/>${item.senderAddressObj.building}`
                              : ""
                          }`
                        : `${
                            item.receiverAddressRoadWithoutSigungu ||
                            item.receiverAddressWithoutSigungu
                          }${
                            item.receiverAddressObj &&
                            item.receiverAddressObj.building
                              ? `<br/>${item.receiverAddressObj.building}`
                              : ""
                          }`
                      : ""
                  } <span style="${orderingNumberColor === "red" ? "color: red" : ""}">
                   ${
                     showMarkerAddress !== "NONE" &&
                     orderingNumbersByDeliveryRiderId.length
                       ? `[${orderingNumbersByDeliveryRiderId.join("] [")}]`
                       : ""
                   }
                   </span>
                  ${effectiveOrder ? `(#${effectiveOrder})` : ""}
                  ${
                    item.renderEstimatedDeliveredAt()
                      ? ` ${item.renderEstimatedDeliveredAt()}`
                      : ""
                  }
                  ${item.renderRequestedDeliveryTimeOnMarker ? `<br />${item.renderRequestedDeliveryTimeOnMarker}` : ""}  
                  </div>`
                    : ""
                }
                ${showMarkerAddress === "NONE" && item.renderRequestedDeliveryTimeOnMarker ? `<div class="marker-address">${item.renderRequestedDeliveryTimeOnMarker}</div>` : ""}
                ${
                  (isOrderMode &&
                    newOrderList.find((el) => el.bookId === item.bookId)) ||
                  (!isOrderAssignmentPending && !isOrderMode && effectiveOrder)
                    ? `<div class="marker-order">
                      ${getOrderWithOrderMode(
                        isOrderMode,
                        orderList,
                        newOrderList,
                        effectiveOrder,
                        item.bookId,
                      )}
                    </div>`
                    : ""
                }
                ${memoForCjoyquickapi ? `<div class="marker-memo cjoyquickapi">${memoForCjoyquickapi}</div>` : ""}
                ${memoForKurly ? `<div class="marker-memo kurly">${memoForKurly}</div>` : ""}
              </div>
            `,
        };

        // 마커
        let marker = new window.naver.maps.Marker({
          map: map,
          position: new window.naver.maps.LatLng(item.latitude, item.longitude),
          title: item.bookId,
          icon: markerIcon,
          zIndex: 100,
        });

        marker.status = item.status;
        marker.order = isRearrangeMode
          ? getRearrange(isRearrangeMode, selectedDeliveries, item.bookId)
          : getOrder(
              isOrderMode,
              orderList,
              newOrderList,
              effectiveOrder,
              item.bookId,
            );
        marker.bookId = item.bookId;
        marker.receiverAddress = item.receiverAddress;
        marker.samePlaces = item.samePlaces;
        marker.samePlacesPickup = item.samePlacesPickup;
        marker.statusString = item.statusString;
        marker.statusRearrangeKey = item.statusRearrangeKey;
        marker.reallocatedRiderId = item.reallocatedRiderId;
        marker.corpUserEnableRiderLocationTracking =
          item.corpUserEnableRiderLocationTracking;

        marker.soojioneLimitedCustomized = item.soojioneLimitedCustomized;
        marker.soojioneLimitedCustomizedLngLat =
          item.soojioneLimitedCustomizedLngLat;
        marker.statusAddressNotSupported = item.statusAddressNotSupported;
        marker.soojioneLimitedCustomizedAddressRoadWithoutSigungu =
          item.soojioneLimitedCustomizedAddressRoadWithoutSigungu;
        marker.latitude = item.latitude;
        marker.longitude = item.longitude;

        marker.delivery = item;

        // 툴팁
        let infoWindow = new window.naver.maps.InfoWindow({
          content: infoWindowContent,
        });

        markers.push(marker);
        infoWindows.push(infoWindow);
      });

      updateMarkers(map, markers);

      window.naver.maps.Event.addListener(map, "idle", function () {
        updateMarkers(map, markers);
      });

      for (var j = 0, jj = markers.length; j < jj; j++) {
        window.naver.maps.Event.addListener(
          markers[j],
          "click",
          getClickHandler(j),
        );
      }

      window.naver.maps.Event.addListener(map, "click", function () {
        for (var i = 0; i < markers.length; i++) {
          var infoWindow = infoWindows[i];

          if (infoWindow.getMap()) {
            infoWindow.close();

            //  폴리라인 삭제
            for (let i = 0; i < polylines.length; i++) {
              polylines[i].setMap(null);
            }

            // 건물입구점 폴리라인 삭제
            for (let i = 0; i < polylinesBaseAddressEntryPoint.length; i++) {
              polylinesBaseAddressEntryPoint[i].setMap(null);
            }

            // 수지원 마커 삭제
            for (let i = 0; i < markersSoojione.length; i++) {
              markersSoojione[i].setMap(null);
            }

            // 건물입구점 마커 삭제
            for (let i = 0; i < markersBaseAddressEntryPoint.length; i++) {
              markersBaseAddressEntryPoint[i].setMap(null);
            }
          }
        }
      });

      if (window.ReactNativeWebView) {
        if (appVersion) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: MESSAGE_UPDATED }),
          );
        } else {
          window.ReactNativeWebView.postMessage(MESSAGE_UPDATED);
        }
      }
    } else {
      if (window.ReactNativeWebView) {
        if (appVersion) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: MESSAGE_UPDATED }),
          );
        } else {
          window.ReactNativeWebView.postMessage(MESSAGE_UPDATED);
        }
      }
    }
  };

  useEffect(() => {
    if (map && fetched) {
      drawMarker();
    }
  }, [
    isOrderMode,
    fetched,
    map,
    markerList,
    newOrderList,
    selectedDeliveries,
    showMarkerAddress,
    orderingNumberColor,
    linkOthers,
    ...(Number(appVersion) >= 46
      ? [scanSessions] // ✅ 46 이상
      : [deliveryStartBookIdsObj, deliveryStartBookIds]), // 46 미만 기존 유지
    deliveryRoutesMap,
  ]);

  function showMarker(map, marker) {
    if (marker.setMap()) return;
    marker.setMap(map);
  }

  function hideMarker(marker) {
    if (!marker.setMap()) return;
    marker.setMap(null);
  }

  // 배송 순서 지정
  const handleClickOrderDeliveries = () => {
    if (deliveryStartList.length !== newOrderList.length) {
      openModal(
        <OrderConfirmModal
          key="order-confirm-modal"
          changeOrder={submitOrderDeliveries}
        />,
      );
    } else {
      submitOrderDeliveries();
    }
  };

  const submitOrderDeliveries = async () => {
    try {
      startLoading();
      let _orderList = [...newOrderList];

      deliveryStartList.forEach((deliver) => {
        let index = _orderList.findIndex((el) => el.bookId === deliver.bookId);
        if (index < 0)
          _orderList.push({
            bookId: deliver.bookId,
            order: null,
          });
      });

      const data = {
        orders: _orderList,
      };

      await orderActions.reorderDeliveries(data);

      toast(`순서 지정을 저장하였습니다.`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
      });

      cancelOrderMode({ resetStatuses: true });

      handleChangeReload(true);
    } catch (e) {
      alert(`순서 지정에 실패하였습니다.. \n에러메시지: ${e.message}`);
    }

    finishLoading();
  };

  // 자동순서 지정
  const handleClickSubmitAuto = () => {
    window.alert("현재 이용 불가합니다. ");
    return;
    // openModal(
    //   <SetGoalAddressModal
    //     key="set-goal-address-modal"
    //     onSubmit={handleSubmitSetGoalAddress}
    //   />,
    // );
  };

  // const handleSubmitSetGoalAddress = async (address) => {
  //   try {
  //     setIsOptimizing(true);
  //     startLoading();

  //     const body = {
  //       startCoordinates: [
  //         Number(currentPosition?.longitude || "127.0070008506"),
  //         Number(currentPosition?.latitude || "37.5316064316"),
  //       ],
  //       goalCoordinates: [
  //         Number(address?.long || "127.0070008506"),
  //         Number(address?.lat || "37.5316064316"),
  //       ],
  //       bookIds: deliveryStartList.map((el) => el.bookId),
  //       optimize: true,
  //     };

  //     const response = await routesActions.fetchRoutesOptimize(body);
  //     const routeId = response.id;

  //     if (!routeId) {
  //       throw new Error(
  //         `배송순서 자동생성 요청이 실패했습니다. 인터넷 사용이 가능한 장소에서 재시도해주십시오. ${response.status}`,
  //       );
  //     }

  //     const result = await pollOptimizationResult(routeId);
  //     handleOptimizationSuccess(result);
  //   } catch (e) {
  //     if (isOptimizingRef.current) {
  //       // 로딩 중(2분 이내) 에러 → 기존처럼 alert
  //       window.alert(
  //         `배송순서 자동생성 요청이 실패했습니다. 인터넷 사용이 가능한 장소에서 재시도해주십시오.\n에러메시지: ${e.message}`,
  //       );
  //     } else {
  //       openModal(
  //         <ConfirmModal
  //           key="optimization-error-modal"
  //           description={`배송순서 자동생성에 실패했습니다.\n순서 미지정 상태로 배송을 계속 진행해 주십시오.\n\n에러: ${e.message}`}
  //           onConfirmText="확인"
  //           onConfirm={() => closeModal()}
  //         />,
  //       );
  //     }
  //   } finally {
  //     setIsOptimizing(false);
  //     setIsOptimizingBackground(false);
  //     finishLoading();
  //   }
  // };

  // 수동순서 지정
  const handleClickSubmitManual = async () => {
    if (deliveryStartList.length !== newOrderList.length) {
      window.alert("실배송건을 모두 선택해주세요.");
      return;
    }

    try {
      setIsOptimizing(true);
      startLoading();

      const body = {
        startCoordinates: [
          Number(currentPosition?.longitude || "127.0070008506"),
          Number(currentPosition?.latitude || "37.5316064316"),
        ],
        bookIds: newOrderList.map((el) => el.bookId),
        optimize: false,
      };

      const response = await routesActions.fetchRoutesOptimize(body);
      const routeId = response.id;

      if (!routeId) {
        throw new Error(
          `배송순서 수동생성 요청이 실패했습니다. 인터넷 사용이 가능한 장소에서 재시도해주십시오. ${response.status}`,
        );
      }

      const result = await pollOptimizationResult(routeId);
      handleOptimizationSuccess(result);
    } catch (e) {
      if (isOptimizingRef.current) {
        // 로딩 중(2분 이내) 에러 → 기존처럼 alert
        window.alert(
          `배송순서 수동생성 요청이 실패했습니다. 인터넷 사용이 가능한 장소에서 재시도해주십시오.\n에러메시지: ${e.message}`,
        );
      } else {
        openModal(
          <ConfirmModal
            key="optimization-error-modal"
            description={`배송순서 수동생성에 실패했습니다.\n순서 미지정 상태로 배송을 계속 진행해 주십시오.\n\n에러: ${e.message}`}
            onConfirmText="확인"
            onConfirm={() => closeModal()}
          />,
        );
      }
    } finally {
      setIsOptimizing(false);
      setIsOptimizingBackground(false);
      finishLoading();
    }
  };

  const [optimizationProgress, setOptimizationProgress] = useState(0);

  const pollOptimizationResult = async (routeId) => {
    const MAX_ATTEMPTS = 300;
    let attempts = 0;

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          attempts++;

          // 진행률 업데이트 (예: 예상 시간 기반)
          const progress = Math.min((attempts / 30) * 100, 95); // 최대 95%까지
          setOptimizationProgress(progress);

          const response = await routesActions.pollRoutesOptimize(routeId);

          if (isOptimizationComplete(response)) {
            setOptimizationProgress(100);
            resolve(response);
            return;
          }

          if (response.optimizationError) {
            throw new Error(`최적화 실패: ${response.optimizationError}`);
          }

          if (attempts >= MAX_ATTEMPTS) {
            throw new Error("최적화 시간 초과 (5분)");
          }

          setTimeout(poll, 1000);
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  };

  // 최적화 완료 여부 확인
  const isOptimizationComplete = (data) => {
    return (
      (data.status === "PLANNED" || data.status === "PENDING") &&
      Array.isArray(data.stops) &&
      data.stops.length > 0
    );
  };

  // 최적화 성공 처리
  const handleOptimizationSuccess = (result) => {
    const newRoutesMap = {};
    result.stops.forEach((stop) => {
      newRoutesMap[bookIdKey(stop.bookId)] = {
        routeId: result.id,
        sequence: stop.sequence,
        totalStops: result.stops.length,
        stopStatus: stop.stopStatus || "PENDING",
        routeStatus: result.status,
        tripSequence: result.tripSequence,
        routeDate: result.routeDate,
      };
    });

    setIsOrderAssignmentPending(false);
    setDeliveryRoutesMap(newRoutesMap);
    deliveryRoutesMapRef.current = newRoutesMap;
    setIsOptimizingBackground(false);

    if (isOptimizingRef.current) {
      toast(`배송순서 자동생성을 완료했습니다.`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
        autoClose: 3000,
      });
      cancelOrderMode({ resetStatuses: true });
      closeModal();
    } else {
      openModal(
        <ConfirmModal
          key="optimization-done-modal"
          description="배송순서 지정이 완료되었습니다. 지도에 배송순서가 표시됩니다."
          onConfirmText="확인"
          onConfirm={() => closeModal()}
        />,
      );
    }
  };

  // 출발건제외
  const handleClickSubmitExclude = () => {
    closeModal();
    cancelOrderMode({ resetStatuses: false });

    handleClickStartRearrange({
      onlyDeliveryStart: true,
      rearrangeDescription:
        "이번 회차에 배송하지 않을 배송건은 대기/연기 상태로 변경해 주십시오.",
    });
  };

  // 재배차 모달
  const handleClickRearrangeDeliveries = () => {
    if (selectedDeliveries.length > 0) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "favoRiders" }),
        );
      }

      openModal(
        <RidersModal key="riders-modal" onSubmitAfter={handleChangeReload} />,
      );
    } else {
      toast(`재배차할 배송건을 선택해주세요.`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
      });
    }
  };

  // 배송 대기 시작
  const handleClickPrevDeliveries = async ({
    force = false,
    selectedDeliveriesWithDelay = [],
  }) => {
    let _selectedDeliveries = [];
    let _status = null;
    selectedDeliveriesWithDelay.forEach((el) => {
      if (
        el.status === STATUS_DELIVERY_STARTED ||
        el.status === STATUS_DELIVERY_POSTPONED
      ) {
        _selectedDeliveries.push(el);
        _status = el.status;
      }
    });

    if (_selectedDeliveries.length > 0) {
      if (
        force ||
        window.confirm(
          `${_selectedDeliveries[0].bookId} 포함 ${_selectedDeliveries.length}건을 배송대기 상태로 변경하시겠습니까?`,
        )
      ) {
        prevDeliveries(_selectedDeliveries, _status, force);
      }
    } else {
      toast(`대기 상태로 변경할 배송건을 선택해주세요.`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
      });
    }
  };

  const prevDeliveries = async (_selectedDeliveries, status, force = false) => {
    try {
      const filteredDeliveriesWithoutDelay = _selectedDeliveries.filter(
        (delivery) =>
          delivery.delayedDeliveries?.length === 0 &&
          delivery.status === STATUS_DELIVERY_STARTED &&
          delivery.type === DELIVERY_TYPE_NORMAL,
      );

      if (!force && filteredDeliveriesWithoutDelay.length > 0) {
        openModal(
          <ConfirmModal
            key="confirm-modal"
            description={`${filteredDeliveriesWithoutDelay[0].bookId} 포함 ${filteredDeliveriesWithoutDelay.length}건을 다음회차 배송으로 지연사유 등록하고 연기상태로 변경하시겠습니까?`}
            onConfirm={() => {
              closeModal();

              setDelayBulk({
                bookIds: filteredDeliveriesWithoutDelay.map((el) => el.bookId),
                reason: "다음 회차",
                reasonType: "REASON_NEXT_DELIVERY",
                action: "prev",
              });
            }}
            onCancel={() => {
              closeModal();

              if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(
                  JSON.stringify({
                    type: "delay",
                    deliveries: filteredDeliveriesWithoutDelay,
                    action: "prev",
                    selectedDeliveriesWithDelay: selectedDeliveries.map(
                      (el) => ({ bookId: el.bookId, status: el.status }),
                    ),
                  }),
                );
              }
            }}
            onConfirmText="확인"
            onCancelText="사유개별등록"
          />,
        );
        return;
      }

      startLoading();

      await deliveriesActions.prevDeliveries({
        bookIds: _selectedDeliveries.map((el) => el.bookId),
        status: status === STATUS_DELIVERY_STARTED ? 4 : 12,
      });

      toast(
        `${_selectedDeliveries[0].bookId} 포함 ${_selectedDeliveries.length}건을 배송대기 상태로 변경처리하였습니다.`,
        {
          className: "toast-background",
          bodyClassName: "toast-body",
          progressClassName: "fancy-progress-bar",
        },
      );

      deliveriesActions.setPreviousStatuses();
      reallocationActions.cancelRearrangeMode();

      if (isExcludeModeRef.current) {
        // 출발건제외 후 순서지정 팝업 다시 표시
        isExcludeModeRef.current = false;
        setDeliveryRoutesMap({});
        deliveryRoutesMapRef.current = {};
        lastCheckedDeliveryBookIdsRef.current = new Set();
        setCheckedDeliveryStartedOrders(false);
        isInitializingRoutes.current = false;
        routeInitRetryCountRef.current = 0;
      }

      handleChangeReload(true);
    } catch (e) {
      alert(
        `배송대기 상태로 변경에 실패하였습니다. \n에러메시지: ${e.message}`,
      );
    } finally {
      finishLoading();
    }
  };

  const setDelayBulk = async ({ bookIds, reason, reasonType, action }) => {
    try {
      startLoading();

      await Promise.all(
        bookIds.map((bookId) =>
          deliveriesActions.setDelay(bookId, {
            reason,
            reasonType,
            processType: "delivery",
            latitude: currentPosition?.latitude,
            longitude: currentPosition?.longitude,
          }),
        ),
      );

      // TODO: 지연사유 등록시 문자 발송

      if (action === "prev") {
        handleClickPrevDeliveries({
          force: true,
          selectedDeliveriesWithDelay: selectedDeliveries,
        });
      } else if (action === "postponed") {
        handleClickPostponedDeliveries({
          force: true,
          selectedDeliveriesWithDelay: selectedDeliveries,
        });
      }
    } catch (e) {
      alert(`지연사유 등록에 실패하였습니다. \n에러메시지: ${e.message}`);
    } finally {
      finishLoading();
    }
  };

  // 배송 연기 시작
  const handleClickPostponedDeliveries = async ({
    force = false,
    selectedDeliveriesWithDelay = [],
  }) => {
    let _selectedDeliveries = [];
    let _status = null;
    selectedDeliveriesWithDelay.forEach((el) => {
      if (
        el.status === STATUS_DELIVERY_STARTED ||
        el.status === STATUS_DELIVERY_WAITING
      ) {
        _selectedDeliveries.push(el);
        _status = el.status;
      }
    });

    if (_selectedDeliveries.length > 0) {
      if (
        force ||
        window.confirm(
          `${_selectedDeliveries[0].bookId} 포함 ${_selectedDeliveries.length}건을 배송연기 상태로 변경하시겠습니까?`,
        )
      ) {
        postponedDeliveries(_selectedDeliveries, _status, force);
      }
    } else {
      toast(`연기 상태로 변경할 배송건을 선택해주세요.`, {
        className: "toast-background",
        bodyClassName: "toast-body",
        progressClassName: "fancy-progress-bar",
      });
    }
  };

  const postponedDeliveries = async (
    _selectedDeliveries,
    status,
    force = false,
  ) => {
    try {
      const filteredDeliveriesWithoutDelay = _selectedDeliveries.filter(
        (delivery) =>
          delivery.delayedDeliveries?.length === 0 &&
          delivery.status === STATUS_DELIVERY_STARTED &&
          delivery.type === DELIVERY_TYPE_NORMAL,
      );

      if (!force && filteredDeliveriesWithoutDelay.length > 0) {
        openModal(
          <ConfirmModal
            key="confirm-modal"
            description={`${filteredDeliveriesWithoutDelay[0].bookId} 포함 ${filteredDeliveriesWithoutDelay.length}건을 다음회차 배송으로 지연사유 등록하고 연기상태로 변경하시겠습니까?`}
            onConfirm={() => {
              closeModal();

              setDelayBulk({
                bookIds: filteredDeliveriesWithoutDelay.map((el) => el.bookId),
                reason: "다음 회차",
                reasonType: "REASON_NEXT_DELIVERY",
                action: "postponed",
              });
            }}
            onCancel={() => {
              closeModal();

              if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(
                  JSON.stringify({
                    type: "delay",
                    deliveries: filteredDeliveriesWithoutDelay,
                    action: "postponed",
                    selectedDeliveriesWithDelay: selectedDeliveries.map(
                      (el) => ({ bookId: el.bookId, status: el.status }),
                    ),
                  }),
                );
              }
            }}
            onConfirmText="확인"
            onCancelText="사유개별등록"
          />,
        );
        return;
      }

      startLoading();

      await Promise.all(
        _selectedDeliveries.map((delivery) =>
          deliveriesActions.setPostponed(delivery.bookId),
        ),
      );

      toast(
        `${_selectedDeliveries[0].bookId} 포함 ${_selectedDeliveries.length}건을 배송연기 상태로 변경처리하였습니다.`,
        {
          className: "toast-background",
          bodyClassName: "toast-body",
          progressClassName: "fancy-progress-bar",
        },
      );

      deliveriesActions.setPreviousStatuses();
      reallocationActions.cancelRearrangeMode();

      if (isExcludeModeRef.current) {
        // 출발건제외 후 순서지정 팝업 다시 표시
        isExcludeModeRef.current = false;
        setDeliveryRoutesMap({});
        deliveryRoutesMapRef.current = {};
        lastCheckedDeliveryBookIdsRef.current = new Set();
        setCheckedDeliveryStartedOrders(false);
        isInitializingRoutes.current = false;
        routeInitRetryCountRef.current = 0;
      }

      handleChangeReload(true);
    } catch (e) {
      alert(
        `배송연기 상태로 변경에 실패하였습니다. \n에러메시지: ${e.message}`,
      );
    } finally {
      finishLoading();
    }
  };

  // 지도 설정
  const handleClickSetting = () => {
    if (isOrderMode || isRearrangeMode) {
      return;
    }

    openModal(
      <SettingModal
        key="setting-modal"
        linkOthers={linkOthers}
        statuses={statuses}
        currentCenter={currentCenter}
        showHideMarkers={showHideMarkers}
        showMarkerAddress={showMarkerAddress}
        orderingNumberColor={orderingNumberColor}
        onSubmit={updateSetting}
      />,
    );
  };

  const updateSetting = (data) => {
    deliveriesActions.setStatuses(data.statuses);
    mapActions.setLinkOthers(data.linkOthers);
    mapActions.getCurrentPosition(data.currentCenter);
    mapActions.setShowHideMarkers(data.showHideMarkers === "true");
    mapActions.setShowMarkerAddress(data.showMarkerAddress);
    mapActions.setOrderingNumberColor(
      data.orderingNumberColor === "red" ? "red" : "black",
    );
    closeModal();
  };

  // 현재 위치 이동
  const handleClickCurrent = () => {
    mapActions.setMapCurrentCenter();
  };

  return (
    <ThemeProvider theme={size ? SIZE[size] : {}}>
      {isOptimizing && (
        <LoadingComponent
          loadingText="배송순서 자동생성 중..."
          progress={optimizationProgress}
          showClose={showLoadingClose}
          onClose={() => {
            setIsOptimizing(false); // 로딩 닫기
            finishLoading();
            setIsOptimizingBackground(true); // 백그라운드 진행 중 표시
            cancelOrderMode({ resetStatuses: true }); // OrderGuide 닫기
          }}
        />
      )}
      {!isOptimizing && loading && <LoadingComponent />}
      {isOptimizingBackground && (
        <OptimizingBackground>배송순서 자동생성 중...</OptimizingBackground>
      )}
      <MapContainer>
        <div className="map">
          {/** 배송 순서 클릭 가이드 */}
          <OrderGuide
            isShow={isOrderMode && !modals.length}
            orderListCount={newOrderList.length}
            deliveriesCount={deliveryStartList.length}
            onClickSubmit={handleClickOrderDeliveries}
            onClickCancel={handleClickCancelOrderMode}
            onClickSubmitAuto={handleClickSubmitAuto}
            onClickSubmitManual={handleClickSubmitManual}
            onClickSubmitExclude={handleClickSubmitExclude}
            deliveriesStartedWithLocationTracking={
              deliveriesStartedWithLocationTracking
            }
            localCurrentCenter={localCurrentCenter}
            onClickCenterToggle={handleClickCenterToggle}
            onClickCurrentLocation={handleClickCurrent}
          />
          {/** 재배차 클릭 가이드 */}
          <RearrangeGuide
            description={rearrangeDescription}
            isShow={isRearrangeMode}
            rearrangeCount={selectedDeliveries.length}
            deliveriesCount={
              selectedDeliveries.length > 0
                ? selectedDeliveries[0].status === STATUS_PICKUP
                  ? count.pickups
                  : selectedDeliveries[0].status === STATUS_DELIVERY_WAITING
                    ? count.deliveriesBeforeStart
                    : selectedDeliveries[0].status === STATUS_DELIVERY_POSTPONED
                      ? count.deliveriesPostponed
                      : count.deliveriesStarted
                : statuses.length === 1 &&
                    statuses.includes(STATUS_DELIVERY_STARTED)
                  ? count.deliveriesStarted
                  : count.pickups + count.deliveriesAllocated
            }
            onClickSubmitRearrange={handleClickRearrangeDeliveries}
            onClickSubmitPrev={() =>
              handleClickPrevDeliveries({
                force: false,
                selectedDeliveriesWithDelay: selectedDeliveries,
              })
            }
            onClickSubmitPostponed={() =>
              handleClickPostponedDeliveries({
                force: false,
                selectedDeliveriesWithDelay: selectedDeliveries,
              })
            }
            onClickCancel={handleClickCancelRearrangeMode}
            localCurrentCenter={localCurrentCenter}
            onClickCenterToggle={handleClickCenterToggle}
            onClickCurrentLocation={handleClickCurrent}
          />
          <TopWrap hide={modals.length > 0 || isOrderMode || isRearrangeMode}>
            <CountSummary count={count} limitedHoursCount={limitedHoursCount} />
          </TopWrap>
          <BottomWrap
            hide={modals.length > 0 || isOrderMode || isRearrangeMode}
          >
            <BottomSummary
              statuses={statuses}
              linkOthers={linkOthers}
              currentCenter={currentCenter}
              showHideMarkers={showHideMarkers}
              showMarkerAddress={showMarkerAddress}
              orderingNumberColor={orderingNumberColor}
            />
            <BottomButtonsWrap>
              {/** 설정 */}
              <ButtonSetting onClick={handleClickSetting}>
                <IconSetting />
              </ButtonSetting>
              {/** 지도 하단 : 순서지정/재배차 버튼 */}
              <BottomButtons
                onClickStartOrder={handleClickStartOrder}
                onClickStartRearrange={handleClickStartRearrange}
              />
              {/** 현재위치 */}
              <ButtonCurrent onClick={handleClickCurrent}>
                <IconCurrent />
              </ButtonCurrent>
              {/** 새로고침 */}
              <ButtonReload onClick={reloadDeliveries}>
                <IconRefresh />
              </ButtonReload>
            </BottomButtonsWrap>
          </BottomWrap>
          {map && showHideMarkers && (
            <TrackingMarkers
              markers={hideMarkers}
              mapBounds={map.getBounds()}
            />
          )}{" "}
          <MapConatiner id="map" />
          {mapLoadFailed && (
            <MapRetryWrap>
              <div style={{ fontSize: "16px" }}>
                지도를 불러오지 못했습니다.
              </div>
              <MapRetryButton type="button" onClick={startNaverMap}>
                다시 시도
              </MapRetryButton>
            </MapRetryWrap>
          )}
        </div>
      </MapContainer>
    </ThemeProvider>
  );
}

export default Map;
